/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Table } from "semantic-ui-react";
import { MenuItem, Select, TextField } from "@mui/material";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { changeBottomNavigationElements, changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import jsonEquals from "../functions/jsonEquals";
import updateElement, { updateCitedArtifactContributorshipEntryElement, updateCitedArtifactContributorshipSummaryElement, updateCitedArtifactElement, updateCitedArtifactPublicationFormElement } from "../functions/updateElement";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import { displayStringFromFHIRIdentifier, generateIdentifierTable } from "../datatypeComponents/IdentifierEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import PeriodEntry, { DateTimeEntry, displayStringFromFHIRPeriod, getDateTimeDisplay } from "../datatypeComponents/PeriodEntry";
import { DisplayElementNotes } from "../datatypeComponents/AnnotationEntry";
import { DisplayFromFHIRAttachment } from "../datatypeComponents/AttachmentEntry";
import { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import BooleanEntry from "../datatypeComponents/BooleanEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import PositiveIntEntry from "../datatypeComponents/PositiveIntEntry";
import CodeArrayEntry from "../datatypeComponents/CodeArrayEntry";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, getCodeableConceptDisplayForObjectOrArray, simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import generateComputablePublishingSummary from "../functions/generateComputablePublishingSummaryFromCitation";
import DropDownWithAdditions from "../components/DropDownWithAdditions";
import {
    abstractTypeLookup, articleUrlTypeLookup, identifierDisplayLookup,
    identifierSystemLookup, languageCodeDisplay, languageCodeOptions, languageCodes,
    startingIdentifierTypeOptions, titleTypeLookup
} from "../dictionaries/CodeSystemLookup";
import {
    artifactContributionInstanceTypeValueSet, citedArtifactContributionTypeValueSet, contributorRoleValueSet,
    contributorSummarySourceValueSet, contributorSummaryTypeValueSet, convertValueSetToCodeableConceptSet,
    generateValueSetLookup, generateValueSetOptions
} from "../dictionaries/THOandFHIRValueSets";
import Spinner from "../components/Spinner";

const CitationClassificationEntry = ({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed }) => {

    const [citationClassificationState, setCitationClassificationState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingCitationClassification = {
            type: "", classifier: []
        }
        if (startingValue) {
            if (startingValue.extension) { startingCitationClassification.extension = startingValue.extension; }
            if (startingValue.type) { startingCitationClassification.type = startingValue.type; }
            if (startingValue.classifier) { startingCitationClassification.classifier = startingValue.classifier; }
        }
        setCitationClassificationState(JSON.parse(JSON.stringify(startingCitationClassification)));
    }, []);

    useEffect((() => {
        if (Object.keys(citationClassificationState).length > 0) {
            let newCitationClassification = {};
            if (citationClassificationState.extension) { newCitationClassification.extension = citationClassificationState.extension; }
            if (citationClassificationState.type && Object.keys(citationClassificationState.type).length > 0) {
                newCitationClassification.type = citationClassificationState.type;
            }
            if (Array.isArray(citationClassificationState.classifier) && citationClassificationState.classifier.length > 0 &&
                Object.keys(citationClassificationState.classifier[0]).length > 0) {
                newCitationClassification.classifier = citationClassificationState.classifier;
            }
            if (Object.keys(newCitationClassification).length === 0) {
                newCitationClassification = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newCitationClassification }; });
        }
    }), [citationClassificationState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.type} /></span>}
                {startingValue.classifier && <p><b>Classifier: </b>{getCodeableConceptDisplayForObjectOrArray(startingValue.classifier)}</p >}
            </div >}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div >
    } else if (Object.keys(citationClassificationState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='type'
                    fieldLabel='Type'
                    startingValue={citationClassificationState.type}
                    codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet("citationClassificationTypeValueSet")}
                    setResourceState={setCitationClassificationState} />
                <ArrayEntry datatype="CodeableConcept" elementName='classifier'
                    fieldLabel='Classifier' deletable deletableArray
                    startingValue={citationClassificationState.classifier}
                    codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet("citationArtifactClassifierValueSet")}
                    setResourceState={setCitationClassificationState} />
            </div>
        </>
    }
};

const CitationStatusDateEntry = ({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed }) => {

    const [citationStatusDateState, setCitationStatusDateState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingCitationStatusDate = {
            activity: "", actual: "", period: ""
        }
        if (startingValue) {
            if (startingValue.extension) { startingCitationStatusDate.extension = startingValue.extension; }
            if (startingValue.activity) { startingCitationStatusDate.activity = startingValue.activity; }
            if (typeof startingValue.actual === "boolean") { startingCitationStatusDate.actual = startingValue.actual; }
            if (startingValue.period) { startingCitationStatusDate.period = startingValue.period; }
        }
        setCitationStatusDateState(JSON.parse(JSON.stringify(startingCitationStatusDate)));
    }, []);

    useEffect((() => {
        if (Object.keys(citationStatusDateState).length > 0) {
            let newCitationStatusDate = {};
            if (citationStatusDateState.extension) { newCitationStatusDate.extension = citationStatusDateState.extension; }
            if (citationStatusDateState.activity && Object.keys(citationStatusDateState.activity).length > 0) {
                newCitationStatusDate.activity = citationStatusDateState.activity;
            }
            if (typeof citationStatusDateState.actual === "boolean") {
                newCitationStatusDate.actual = citationStatusDateState.actual;
            }
            if (citationStatusDateState.period && Object.keys(citationStatusDateState.period).length > 0) {
                newCitationStatusDate.period = citationStatusDateState.period;
            }
            if (Object.keys(newCitationStatusDate).length === 0) {
                newCitationStatusDate = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newCitationStatusDate }; });
        }
    }), [citationStatusDateState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.activity && <span><b>Citation Status: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.activity} /></span>}
                {startingValue.actual === true && <p><b>Actual</b></p>}
                {startingValue.actual === false && <p><b>Expected</b></p>}
                {startingValue.period && <p><b>Period: </b>{displayStringFromFHIRPeriod(startingValue.period)}</p >}
            </div >}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div >
    } else if (Object.keys(citationStatusDateState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='activity'
                    fieldLabel='Citation Status'
                    startingValue={citationStatusDateState.activity}
                    setResourceState={setCitationStatusDateState} />
                <BooleanEntry elementName='actual'
                    fieldLabel='Actual'
                    startingValue={citationStatusDateState.actual}
                    storeFalse={true}
                    setResourceState={setCitationStatusDateState} />
                <PeriodEntry elementName='period'
                    fieldLabel='Period'
                    startingValue={citationStatusDateState.period}
                    storeFalse={true}
                    setResourceState={setCitationStatusDateState} />
            </div>
        </>
    }
};

const CitationSummaryEntry = ({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed }) => {

    const [citationSummaryState, setCitationSummaryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingCitationSummary = {
            style: "", text: ""
        }
        if (startingValue) {
            if (startingValue.extension) { startingCitationSummary.extension = startingValue.extension; }
            if (startingValue.style) { startingCitationSummary.style = startingValue.style; }
            if (startingValue.text) { startingCitationSummary.text = startingValue.text; }
        }
        setCitationSummaryState(JSON.parse(JSON.stringify(startingCitationSummary)));
    }, []);

    useEffect((() => {
        if (Object.keys(citationSummaryState).length > 0) {
            let newCitationSummary = {};
            if (citationSummaryState.extension) { newCitationSummary.extension = citationSummaryState.extension; }
            if (citationSummaryState.style && Object.keys(citationSummaryState.style).length > 0) {
                newCitationSummary.style = citationSummaryState.style;
            }
            if (citationSummaryState.text) { newCitationSummary.text = citationSummaryState.text; }
            if (Object.keys(newCitationSummary).length === 0) {
                newCitationSummary = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newCitationSummary }; });
        }
    }), [citationSummaryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.style && <span><b>Style: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.style} /></span>}
                {startingValue.text && <span><b>Summary: </b><DisplayFromFHIRMarkdown markdown={startingValue.text} /></span >}
            </div >}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div >
    } else if (Object.keys(citationSummaryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='style'
                    fieldLabel='Style'
                    startingValue={citationSummaryState.style}
                    codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet("citationSummaryStyleValueSet")}
                    setResourceState={setCitationSummaryState} />
                <MarkdownEntry elementName='text' fieldLabel='Summary Text'
                    startingValue={citationSummaryState.text}
                    setResourceState={setCitationSummaryState} />
            </div>
        </>
    }
};

const CitedArtifactPublicationFormArrayEntry = ({ startingValue, setPublicationFormArrayState,
    isJournalArticleCitationProfile, isBookCitationProfile, isBookPartCitationProfile,
    isDatabaseCitationProfile, isDatabaseEntryCitationProfile, isDatasetCitationProfile,
    isPreprintCitationProfile, isSoftwareCitationProfile, isWebPageCitationProfile, foi,
    containedResourceDictionary = {}
}) => {

    const [arrayState, setArrayState] = useState({});
    const [startingArrayState, setStartingArrayState] = useState({});
    const [undoArrayState, setUndoArrayState] = useState(null);

    useEffect(() => {
        let startingArrayAsObject = {};
        if (startingValue) {
            for (let itemIndex in startingValue) {
                startingArrayAsObject[itemIndex] = startingValue[itemIndex];
            }
        }
        if (Object.keys(startingArrayAsObject).length === 0) {
            startingArrayAsObject[0] = {};
        }
        setArrayState(JSON.parse(JSON.stringify(startingArrayAsObject)));
        setStartingArrayState(JSON.parse(JSON.stringify(startingArrayAsObject)));
    }, []);

    useEffect((() => {
        const arrayStateKeysLength = arrayState ? Object.keys(arrayState).length : null;
        if (arrayStateKeysLength > 0 && !jsonEquals(startingArrayState, arrayState)) {
            let newArray = [];
            for (let key of Object.keys(arrayState)) {
                if (arrayState[key] !== null &&
                    !(typeof arrayState[key] === "object" && Object.keys(arrayState[key]).length === 0) &&
                    !(Array.isArray(arrayState[key]) && arrayState[key].length === 0) &&
                    arrayState[key] !== "" &&
                    arrayState[key] !== undefined && arrayState[key] !== "DELETEME") {
                    newArray.push(arrayState[key]);
                }
            }
            setPublicationFormArrayState(newArray);
            if (undoArrayState && (arrayStateKeysLength > 1 || (arrayStateKeysLength === 1 && arrayState['0'] !== "DELETEME"))) {
                setUndoArrayState(null);
            }
        }
    }), [arrayState]);

    useEffect(() => {
        if (undoArrayState) {
            setArrayState({ '0': "DELETEME" });
        }
    }, [undoArrayState]);

    if (Object.keys(arrayState).length > 0) {
        return <div>
            <h3 id="publication-form">Publication Form</h3>
            <div style={{ marginLeft: "24px" }}>
                {Object.entries(arrayState).map((keyValuePair, keyValuePairIndex) => {
                    return <div key={keyValuePairIndex}
                        style={{ marginBottom: "0px" }}>
                        {keyValuePair[1] === "DELETEME" ?
                            <>
                                <p style={{ marginBottom: "0px" }}><b>{'Publication Form entry ' + (keyValuePairIndex + 1)}: </b></p>
                                <div style={{ marginLeft: "24px" }}>WILL BE DELETED.</div>
                            </>
                            :
                            <>
                                <p style={{ marginBottom: "0px" }}>
                                    <b>{'Publication Form entry ' + (keyValuePairIndex + 1)}: </b>
                                    <span style={{ marginLeft: "24px" }}>
                                        &nbsp;&nbsp;
                                        <Button className="formButton" style={{ color: "#000000" }}
                                            content={"x Delete"}
                                            onClick={() => {
                                                setArrayState(prevState => {
                                                    return {
                                                        ...prevState,
                                                        [keyValuePairIndex.toString()]: "DELETEME"
                                                    };
                                                });
                                            }} />
                                    </span>
                                </p>
                                <div style={{ marginLeft: "24px" }}>
                                    <CitedArtifactPublicationFormEntry index={keyValuePair[0]}
                                        fieldLabel={Object.entries(arrayState).length > 1 ?
                                            'Publication Form entry ' + (keyValuePairIndex + 1) + ' of ' + Object.entries(arrayState).length
                                            :
                                            'Publication Form'}
                                        containedResourceDictionary={containedResourceDictionary}
                                        startingValue={keyValuePair[1]} setArrayState={setArrayState}
                                        isJournalArticleCitationProfile={isJournalArticleCitationProfile}
                                        isBookCitationProfile={isBookCitationProfile}
                                        isBookPartCitationProfile={isBookPartCitationProfile}
                                        isDatabaseCitationProfile={isDatabaseCitationProfile}
                                        isDatabaseEntryCitationProfile={isDatabaseEntryCitationProfile}
                                        isDatasetCitationProfile={isDatasetCitationProfile}
                                        isPreprintCitationProfile={isPreprintCitationProfile}
                                        isSoftwareCitationProfile={isSoftwareCitationProfile}
                                        isWebPageCitationProfile={isWebPageCitationProfile}
                                        foi={foi} />
                                </div>
                            </>
                        }
                    </div>
                })}
                <div style={{ marginBottom: "12px" }}>
                    <Button className="formButton" style={{ color: "#000000" }}
                        content={"+ Add Publication Form"}
                        onClick={() => {
                            setArrayState(prevState => {
                                let arrayLength = Object.keys(prevState).length;
                                let arrayIndexNumber = arrayLength.toString();
                                if (prevState['0'] === null) {
                                    arrayIndexNumber = '1';
                                }
                                return {
                                    ...prevState,
                                    [arrayIndexNumber]: ""
                                };
                            });
                        }} />
                    {(Object.keys(arrayState).length !== 0) &&
                        <Button className="formButton" style={{ color: "#000000" }}
                            content={"Delete All Publication Form Entries"}
                            onClick={() => {
                                alert('Deletion will remove all data in this list. When you click Update, the data will be deleted. If you do not want to delete, refresh the page to lose all your edits instead of clicking Update.');
                                setUndoArrayState(arrayState);
                            }} />}
                    {undoArrayState && <Button className="formButton"
                        style={{ color: "#000000", marginLeft: "2px" }}
                        content={"Undo Deletion"}
                        onClick={() => { setArrayState(undoArrayState); }} />}
                </div>
            </div>
        </div>
    }
}

const CitedArtifactPublicationFormPublishedInEntry = ({ startingValue, setPublicationFormState,
    containedResourceDictionary = {}
}) => {

    const [publishedInState, setPublishedInState] = useState({});
    const [startingPublishedInState, setStartingPublishedInState] = useState({});

    useEffect(() => {
        let startingPublishedIn = {
            "type": "", "identifier": [], "title": "", "publisher": "", "publisherLocation": ""
        }
        if (startingValue) {
            if (startingValue.extension) { startingPublishedIn.extension = startingValue.extension; }
            if (startingValue.type) {
                let startingType = startingValue.type;
                if (Array.isArray(startingType.coding)) {
                    for (let coding of startingType.coding) {
                        if (coding.system === "http://hl7.org/fhir/published-in-type" ||
                            coding.system === "http://terminology.hl7.org/CodeSystem/published-in-type") {
                            coding.system = "https://www.nlm.nih.gov/mesh";
                        }
                    }
                }
                startingPublishedIn.type = startingType;
            }
            if (startingValue.identifier) { startingPublishedIn.identifier = startingValue.identifier; }
            if (startingValue.title) { startingPublishedIn.title = startingValue.title; }
            if (startingValue.publisher) { startingPublishedIn.publisher = startingValue.publisher; }
            if (startingValue.publisherLocation) { startingPublishedIn.publisherLocation = startingValue.publisherLocation; }
        }
        setPublishedInState(JSON.parse(JSON.stringify(startingPublishedIn)));
        setStartingPublishedInState(JSON.parse(JSON.stringify(startingPublishedIn)));
    }, []);

    useEffect((() => {
        if (Object.keys(publishedInState).length > 0 && !jsonEquals(publishedInState, startingPublishedInState)) {
            let newPublishedIn = {};
            if (publishedInState.extension) { newPublishedIn.extension = publishedInState.extension; }
            if (publishedInState.type) { newPublishedIn.type = publishedInState.type; }
            if (publishedInState.identifier !== null && publishedInState.identifier !== undefined &&
                Array.isArray(publishedInState.identifier) && publishedInState.identifier.length !== 0) {
                newPublishedIn.identifier = publishedInState.identifier;
            }
            if (publishedInState.title) { newPublishedIn.title = publishedInState.title; }
            if (publishedInState.publisher) { newPublishedIn.publisher = publishedInState.publisher; }
            if (publishedInState.publisherLocation) { newPublishedIn.publisherLocation = publishedInState.publisherLocation; }
            if (Object.keys(newPublishedIn).length === 0) {
                newPublishedIn = null;
            }
            setPublicationFormState(prevState => { return { ...prevState, "publishedIn": newPublishedIn } });
        }
    }), [publishedInState]);

    if (Object.keys(publishedInState).length > 0) {
        return <div>
            {publishedInState.identifier !== undefined && <>
                <span><b style={{ fontSize: "1.1em" }}>Published In:</b></span>
                <div className={`CitationAbstractsSubSection`} style={{ paddingLeft: "40px" }}>
                    <StringEntry elementName='title' fieldLabel='Name'
                        startingValue={publishedInState.title} setResourceState={setPublishedInState} />
                    <CodeableConceptEntry elementName='type'
                        fieldLabel='Type'
                        startingValue={publishedInState.type}
                        codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet("publishedInTypeValueSet")}
                        setResourceState={setPublishedInState} />
                    <ArrayEntry datatype="Identifier" elementName='identifier'
                        fieldLabel='Identifier' deletable={true} deletableArray={true}
                        startCollapsed={true} startEmptyArrayClosed={true}
                        startingValue={publishedInState.identifier}
                        setResourceState={setPublishedInState} />
                    <ReferenceEntry elementName='publisher' fieldLabel='Publisher'
                        startingValue={publishedInState.publisher}
                        containedResourceDictionary={containedResourceDictionary}
                        setResourceState={setPublishedInState} enableCreation={true}
                        startingResourceType='Organization'
                        referencedResourceTypes={['Organization']} startCollapsed={true} />
                    <StringEntry elementName='publisherLocation' fieldLabel='Publisher Location'
                        startingValue={publishedInState.publisherLocation} setResourceState={setPublishedInState} />
                </div>
            </>}
        </div>
    }
}

const CitedArtifactPublicationFormEntry = ({ index, fieldLabel, startingValue, setArrayState,
    isJournalArticleCitationProfile, isBookCitationProfile, isBookPartCitationProfile,
    isDatabaseCitationProfile, isDatabaseEntryCitationProfile, isDatasetCitationProfile,
    isPreprintCitationProfile, isSoftwareCitationProfile, isWebPageCitationProfile, foi,
    containedResourceDictionary = {} }) => {
    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    //TODO -- add support for viewing older versions here

    const [publicationFormState, setPublicationFormState] = useState({});
    const [citedMediumOptions, setCitedMediumOptions] = useState({ "options": generateValueSetOptions("citedMediumValueSet") });

    useEffect(() => {
        let startingPublicationForm = {
            "publishedIn": "", "citedMedium": "", "volume": "",
            "issue": "", "articleDate": "", "publicationDateText": "", "publicationDateSeason": "",
            "lastRevisionDate": "", "language": [], "accessionNumber": "", "pageString": "", "firstPage": "",
            "lastPage": "", "pageCount": "", "copyright": ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingPublicationForm.extension = startingValue.extension; }
            if (startingValue.publishedIn) { startingPublicationForm.publishedIn = startingValue.publishedIn; }
            if (startingValue.citedMedium) {
                let startingCitedMedium = JSON.parse(JSON.stringify(startingValue.citedMedium));
                if (Array.isArray(startingCitedMedium.coding)) {
                    for (let coding of startingCitedMedium.coding) {
                        if (coding.system === "http://hl7.org/fhir/cited-medium") {
                            coding.system = "http://terminology.hl7.org/CodeSystem/cited-medium"
                        }
                        if (coding.code === "Internet") {
                            coding.code = "internet"
                        } else if (coding.code === "Print") {
                            coding.code = "print"
                        } else if (coding.code === "Internet-without-issue") {
                            coding.code = "internet-without-issue"
                        }
                    }
                }
                startingPublicationForm.citedMedium = startingCitedMedium;
            }
            if (startingValue.volume) { startingPublicationForm.volume = startingValue.volume; }
            if (startingValue.issue) { startingPublicationForm.issue = startingValue.issue; }
            if (startingValue.articleDate) { startingPublicationForm.articleDate = startingValue.articleDate; }
            if (startingValue.publicationDateText) { startingPublicationForm.publicationDateText = startingValue.publicationDateText; }
            if (startingValue.publicationDateSeason) { startingPublicationForm.publicationDateSeason = startingValue.publicationDateSeason; }
            if (startingValue.lastRevisionDate) { startingPublicationForm.lastRevisionDate = startingValue.lastRevisionDate; }
            if (startingValue.language) {
                let startingLanguage = [];
                if (!Array.isArray(startingValue.language)) {
                    startingLanguage = [startingValue.language];
                } else {
                    startingLanguage = startingValue.language;
                }
                let revisedStartingLanguage = startingLanguage.map(item => {
                    if (typeof item === "object") {
                        return item.coding?.[0]?.code || item.text || item.toString();
                    } else {
                        return item;
                    }
                });
                startingPublicationForm.language = revisedStartingLanguage;
            }
            if (startingValue.accessionNumber) { startingPublicationForm.accessionNumber = startingValue.accessionNumber; }
            if (startingValue.pageString) { startingPublicationForm.pageString = startingValue.pageString; }
            if (startingValue.firstPage) { startingPublicationForm.firstPage = startingValue.firstPage; }
            if (startingValue.lastPage) { startingPublicationForm.lastPage = startingValue.lastPage; }
            if (startingValue.pageCount) { startingPublicationForm.pageCount = startingValue.pageCount; }
            if (startingValue.copyright) { startingPublicationForm.copyright = startingValue.copyright; }
        }
        setPublicationFormState(JSON.parse(JSON.stringify(startingPublicationForm)));
    }, []);

    useEffect((() => {
        if (Object.keys(publicationFormState).length) {
            let newPublicationForm = {};
            if (publicationFormState.extension) { newPublicationForm.extension = publicationFormState.extension; }
            if (publicationFormState.publishedIn) { newPublicationForm.publishedIn = publicationFormState.publishedIn; }
            if (publicationFormState.citedMedium) { newPublicationForm.citedMedium = publicationFormState.citedMedium; }
            if (publicationFormState.volume) { newPublicationForm.volume = publicationFormState.volume; }
            if (publicationFormState.issue) { newPublicationForm.issue = publicationFormState.issue; }
            if (publicationFormState.articleDate) { newPublicationForm.articleDate = publicationFormState.articleDate; }
            if (publicationFormState.publicationDateText) { newPublicationForm.publicationDateText = publicationFormState.publicationDateText; }
            if (publicationFormState.publicationDateSeason) { newPublicationForm.publicationDateSeason = publicationFormState.publicationDateSeason; }
            if (publicationFormState.lastRevisionDate) { newPublicationForm.lastRevisionDate = publicationFormState.lastRevisionDate; }
            if (publicationFormState.language !== null && publicationFormState.language !== undefined &&
                Array.isArray(publicationFormState.language) && publicationFormState.language.length !== 0) {
                newPublicationForm.language = publicationFormState.language;
            }
            if (publicationFormState.accessionNumber) { newPublicationForm.accessionNumber = publicationFormState.accessionNumber; }
            if (publicationFormState.pageString) { newPublicationForm.pageString = publicationFormState.pageString; }
            if (publicationFormState.firstPage) { newPublicationForm.firstPage = publicationFormState.firstPage; }
            if (publicationFormState.lastPage) { newPublicationForm.lastPage = publicationFormState.lastPage; }
            if (publicationFormState.pageCount) { newPublicationForm.pageCount = publicationFormState.pageCount; }
            if (publicationFormState.copyright) { newPublicationForm.copyright = publicationFormState.copyright; }
            if (Object.keys(newPublicationForm).length === 0) {
                newPublicationForm = null;
            }
            setArrayState(prevState => { return { ...prevState, [index]: newPublicationForm } });
        }
    }), [publicationFormState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "publishedIn",
                "CitationCitedArtifactPublicationFormPublishedIn",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.publishedIn]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "citedMedium",
                "CodeableConcept",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.citedMedium]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "volume",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.volume]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "issue",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.issue]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "articleDate",
                "dateTime",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.articleDate]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "publicationDateText",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.publicationDateText]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "publicationDateSeason",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.publicationDateSeason]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "lastRevisionDate",
                "dateTime",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.lastRevisionDate]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "language",
                "ArrayOfCode",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.language]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "accessionNumber",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.accessionNumber]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "pageString",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.pageString]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "firstPage",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.firstPage]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "lastPage",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.lastPage]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "pageCount",
                "string",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.pageCount]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactPublicationFormElement(publicationFormState, index, "copyright",
                "markdown",
                parentResourceDetail, dispatch);
        }
    }, [publicationFormState.copyright]);

    if (Object.keys(publicationFormState).length > 0) {
        return <div>
            {publicationFormState.publishedIn !== undefined && <>
                <h3>{fieldLabel}</h3>
                <div className={`CitationAbstractsSubSection`} style={{ paddingLeft: "40px" }}>
                    <CitedArtifactPublicationFormPublishedInEntry startingValue={publicationFormState.publishedIn}
                        containedResourceDictionary={containedResourceDictionary}
                        setPublicationFormState={setPublicationFormState} />
                    <span><b style={{ fontSize: "1.1em" }}>Cited Medium: </b>
                        <>
                            <DropDownWithAdditions currentValue={publicationFormState.citedMedium || ''}
                                labelText="Cited Medium" options={citedMediumOptions.options}
                                optionsLookup={generateValueSetLookup("citedMediumValueSet")}
                                setOptions={setCitedMediumOptions}
                                setResourceElementState={setPublicationFormState}
                                index={index} elementName="citedMedium"
                                multiple={false} />
                            <br />
                        </>
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Volume: </b>
                        <TextField style={{ width: "400px", maxWidth: "80%" }}
                            multiline className="inputField" type='text' size="small" variant='outlined'
                            value={publicationFormState.volume || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "volume": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Issue: </b>
                        <TextField style={{ width: "400px", maxWidth: "80%" }}
                            multiline className="inputField" type='text' size="small" variant='outlined'
                            value={publicationFormState.issue || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "issue": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Date of Publication:</b></span>
                    <div className={`CitationAbstractsSubSection`} style={{ paddingLeft: "40px" }}>
                        <span>
                            <TextField style={{ width: "140px", maxWidth: "80%" }} multiline className="inputField"
                                type='text' label={'YYYY-MM-DD'} size="small" variant='outlined'
                                value={publicationFormState.articleDate || ''}
                                onChange={(e) => {
                                    setPublicationFormState(prevState => {
                                        return {
                                            ...prevState,
                                            "articleDate": e.target.value
                                        }
                                    });
                                }} />
                            &nbsp;&nbsp;&nbsp;
                            <TextField style={{ width: "180px", maxWidth: "80%" }} multiline className="inputField"
                                type='text' label={'Season'} size="small" variant='outlined'
                                value={publicationFormState.publicationDateSeason || ''}
                                onChange={(e) => {
                                    setPublicationFormState(prevState => {
                                        return {
                                            ...prevState,
                                            "publicationDateSeason": e.target.value
                                        }
                                    });
                                }} />
                            &nbsp;&nbsp;&nbsp;
                            <TextField style={{ width: "240px", maxWidth: "80%" }} multiline className="inputField"
                                type='text' label={'Text'} size="small" variant='outlined'
                                value={publicationFormState.publicationDateText || ''}
                                onChange={(e) => {
                                    setPublicationFormState(prevState => {
                                        return {
                                            ...prevState,
                                            "publicationDateText": e.target.value
                                        }
                                    });
                                }} />
                            <br />
                        </span>
                        <br />
                    </div>
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Article last revision date: </b>
                        <TextField style={{ width: "260px", maxWidth: "80%" }}
                            multiline className="inputField" type='text' label={'YYYY-MM-DD'}
                            size="small" variant='outlined'
                            value={publicationFormState.lastRevisionDate || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "lastRevisionDate": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Language: </b>
                        <CodeArrayEntry elementName="language" fieldLabel="Language"
                            startingValue={publicationFormState.language || []} setResourceState={setPublicationFormState}
                            allowedValues={languageCodes} dataEntryStyle="dropdownsearch" />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Accession number: </b>
                        <TextField style={{ width: "260px", maxWidth: "80%" }} multiline
                            className="inputField" type='text' size="small" variant='outlined'
                            value={publicationFormState.accessionNumber || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "accessionNumber": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Pages: </b>
                        <TextField style={{ width: "260px", maxWidth: "80%" }} multiline
                            className="inputField" type='text' size="small" variant='outlined'
                            value={publicationFormState.pageString || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "pageString": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>First Page: </b>
                        <TextField style={{ width: "260px", maxWidth: "80%" }} multiline
                            className="inputField" type='text' size="small" variant='outlined'
                            value={publicationFormState.firstPage || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "firstPage": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Last Page: </b>
                        <TextField style={{ width: "260px", maxWidth: "80%" }} multiline
                            className="inputField" type='text' size="small" variant='outlined'
                            value={publicationFormState.lastPage || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "lastPage": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Page count: </b>
                        <TextField style={{ width: "260px", maxWidth: "80%" }} multiline
                            className="inputField" type='text' size="small" variant='outlined'
                            value={publicationFormState.pageCount || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "pageCount": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                    <br />
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Copyright: </b>
                        <TextField style={{ width: "260px", maxWidth: "80%" }} multiline
                            className="inputField" type='text' size="small" variant='outlined'
                            value={publicationFormState.copyright || ''}
                            onChange={(e) => {
                                setPublicationFormState(prevState => {
                                    return {
                                        ...prevState,
                                        "copyright": e.target.value
                                    }
                                });
                            }} />
                        <br />
                    </span>
                </div>
                <br />
            </>}
        </div>
    }
}

const CitedArtifactPublicationFormDisplay = ({ publicationForms, containedResourceDictionary = {} }) => {

    return <div>
        {publicationForms === undefined || publicationForms.length === 0 ?
            <div>
                <h3 id="publication-form">Publication Form</h3>
                <span style={{ color: "#EE1111", paddingLeft: "40px" }}>No data.</span>
            </div>
            :
            <>
                {(publicationForms.length > -1) && publicationForms?.map((pfEntry, index) => {
                    return <div key={index}>
                        <h3>Publication Form
                            {publicationForms.length === 1 ? <></> :
                                <span> ({index + 1} of {publicationForms.length})</span>}
                        </h3>
                        <div className={`CitationAbstractsSubSection`} style={{ paddingLeft: "40px" }}>
                            {pfEntry?.publishedIn && <>
                                <span><b style={{ fontSize: "1.1em" }}>Published In:</b></span>
                                <div className={`CitationAbstractsSubSection`} style={{ paddingLeft: "40px" }}>
                                    {pfEntry.publishedIn.title && <>
                                        <span>
                                            <b style={{ fontSize: "1.1em" }}>Name: </b>{pfEntry.publishedIn.title}
                                        </span>
                                        <br />
                                    </>}
                                    {pfEntry.publishedIn.type && <>
                                        <span>
                                            <b style={{ fontSize: "1.1em" }}>Type: </b>{simpleDisplayStringFromFHIRCodeableConcept(pfEntry.publishedIn.type)}
                                        </span>
                                        <br />
                                    </>}
                                    {pfEntry.publishedIn.identifier?.length > 0 && <>
                                        <span><b style={{ fontSize: "1.1em" }}>Identifiers:</b></span>
                                        <div className={`CitationAbstractsSubSection`} style={{ paddingLeft: "40px" }}>
                                            {pfEntry.publishedIn.identifier.map((identifier, idIndex) => {
                                                return <span key={idIndex}><p>{displayStringFromFHIRIdentifier(identifier)}</p></span>
                                            })}
                                        </div>
                                    </>}
                                    {pfEntry.publishedIn.publisher && <>
                                        <span>
                                            <b style={{ fontSize: "1.1em" }}>Publisher: </b>
                                            <DisplayFromFHIRReference reference={pfEntry.publishedIn.publisher}
                                                containedResourceJson={pfEntry.publishedIn.publisher.reference && containedResourceDictionary[pfEntry.publishedIn.publisher.reference]} />
                                            <br />
                                        </span>
                                    </>}
                                    {pfEntry.publishedIn.publisherLocation && <>
                                        <span>
                                            <b style={{ fontSize: "1.1em" }}>Publisher Location: </b>
                                            {pfEntry.publishedIn.publisherLocation}
                                        </span>
                                    </>}
                                </div>
                            </>}
                            {pfEntry?.citedMedium && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Cited Medium: </b>
                                    {simpleDisplayStringFromFHIRCodeableConcept(pfEntry.citedMedium)}
                                </span>
                                <br />
                            </>}
                            {pfEntry?.volume && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Volume: </b>
                                    {pfEntry.volume}
                                </span>
                                <br />
                            </>}
                            {pfEntry?.issue && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Issue: </b>
                                    {pfEntry.issue}
                                </span>
                                <br />
                            </>}
                            {(pfEntry &&
                                (pfEntry.articleDate || pfEntry.publicationDateText || pfEntry.publicationDateSeason)) &&
                                <>
                                    <span><b style={{ fontSize: "1.1em" }}>Date of Publication:</b></span>
                                    <div className={`CitationAbstractsSubSection`} style={{ paddingLeft: "40px" }}>
                                        <span>
                                            {pfEntry.articleDate && <> <b>Article publication date:</b> {getDateTimeDisplay(pfEntry.articleDate)}</>}
                                            {pfEntry.publicationDateSeason && <> <b>Season:</b> {pfEntry.publicationDateSeason}</>}
                                            {pfEntry.publicationDateText && <> <b>Text:</b> {pfEntry.publicationDateText}</>}
                                        </span>
                                        <br />
                                    </div>
                                </>
                            }
                            {pfEntry?.lastRevisionDate && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Article last revision date: </b>
                                    {getDateTimeDisplay(pfEntry.lastRevisionDate)}</span>
                                <br />
                            </>
                            }
                            {pfEntry?.language?.length > 0 && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Language: </b>
                                    {pfEntry.language.map((pfLanguage) => {
                                        if (typeof pfLanguage === "object") {
                                            if (languageCodeDisplay[pfLanguage.coding?.[0]?.code]) {
                                                return languageCodeDisplay[pfLanguage.coding[0].code];
                                            } else if (pfLanguage.text) {
                                                return "Unrecognized language: " + pfLanguage.text;
                                            } else {
                                                return "Unrecognized language: " + pfLanguage.toString();
                                            }
                                        } else if (languageCodeDisplay[pfLanguage]) {
                                            return languageCodeDisplay[pfLanguage]
                                        } else {
                                            return "Unrecognized language: " + pfLanguage;
                                        }
                                    }).join(", ")}
                                </span>
                                <br />
                            </>}
                            {pfEntry?.accessionNumber && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Accession number: </b>{pfEntry.accessionNumber}
                                </span>
                                <br />
                            </>}
                            {(pfEntry?.pageString || pfEntry?.firstPage || pfEntry?.lastPage) && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Pages: </b>
                                    {pfEntry.pageString ?
                                        <>{pfEntry.pageString}</>
                                        :
                                        <>{
                                            (pfEntry.firstPage && pfEntry.lastPage) ?
                                                <>{pfEntry.firstPage} to {pfEntry.lastPage}</>
                                                :
                                                <>{pfEntry.firstPage}{pfEntry.lastPage}</>
                                        }</>
                                    }
                                </span>
                                <br />
                            </>}
                            {pfEntry?.pageCount && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Page count: </b>{pfEntry.pageCount}
                                </span>
                                <br />
                            </>}
                            {pfEntry?.copyright && <>
                                <span>
                                    <b style={{ fontSize: "1.1em" }}>Copyright: </b><DisplayFromFHIRMarkdown markdown={pfEntry.copyright} />
                                </span>
                            </>}
                        </div>
                        <br />
                    </div>
                })
                }
            </>
        }
    </div>
}

const CitationCitedArtifactContributorshipSummaryEntry = ({
    elementName, startingValue, setResourceState, foi }) => {
    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));

    const [contributorshipSummaryState, setContributorshipSummaryState] = useState({});

    useEffect(() => {
        let startingContributorshipSummary = {
            "type": "", "style": [], "source": "", "value": ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingContributorshipSummary.extension = startingValue.extension; }
            if (startingValue.type) { startingContributorshipSummary.type = startingValue.type; }
            if (startingValue.style) { startingContributorshipSummary.style = startingValue.style; }
            if (startingValue.source) { startingContributorshipSummary.source = startingValue.source; }
            if (startingValue.value) { startingContributorshipSummary.value = startingValue.value; }
        }
        setContributorshipSummaryState(JSON.parse(JSON.stringify(startingContributorshipSummary)));
    }, []);

    useEffect((() => {
        if (Object.keys(contributorshipSummaryState).length) {
            let newContributorship = {};
            if (contributorshipSummaryState.extension) { newContributorship.extension = contributorshipSummaryState.extension; }
            if (contributorshipSummaryState.type) {
                newContributorship.type = contributorshipSummaryState.type;
            }
            if (contributorshipSummaryState.style !== null && contributorshipSummaryState.style !== undefined &&
                Array.isArray(contributorshipSummaryState.style) && contributorshipSummaryState.style.length !== 0) {
                newContributorship.style = contributorshipSummaryState.style;
            }
            if (contributorshipSummaryState.source) {
                newContributorship.source = contributorshipSummaryState.source;
            }
            if (contributorshipSummaryState.value) {
                newContributorship.value = contributorshipSummaryState.value;
            }
            if (Object.keys(newContributorship).length === 0) {
                newContributorship = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newContributorship } });
        }
    }), [contributorshipSummaryState]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipSummaryElement(contributorshipSummaryState, elementName,
                "type", parentResourceDetail, dispatch);
        }
    }, [contributorshipSummaryState.type]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipSummaryElement(contributorshipSummaryState, elementName,
                "style", parentResourceDetail, dispatch);
        }
    }, [contributorshipSummaryState.style]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipSummaryElement(contributorshipSummaryState, elementName,
                "source", parentResourceDetail, dispatch);
        }
    }, [contributorshipSummaryState.source]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipSummaryElement(contributorshipSummaryState, elementName,
                "value", parentResourceDetail, dispatch);
        }
    }, [contributorshipSummaryState.value]);

    return <>{contributorshipSummaryState.style !== undefined &&
        <div>
            <MarkdownEntry elementName='value' fieldLabel='Summary Text'
                startingValue={contributorshipSummaryState.value}
                setResourceState={setContributorshipSummaryState} />
            <CodeableConceptEntry elementName='type'
                fieldLabel='Summary Type' startCollapsed={true}
                startingValue={contributorshipSummaryState.type}
                valueSet={contributorSummaryTypeValueSet}
                setResourceState={setContributorshipSummaryState} />
            <CodeableConceptEntry elementName='source'
                fieldLabel='Summary Source' startCollapsed={true}
                startingValue={contributorshipSummaryState.source}
                valueSet={contributorSummarySourceValueSet}
                setResourceState={setContributorshipSummaryState} />
            <ArrayEntry datatype="CodeableConcept" elementName='style'
                fieldLabel='Summary Style' startCollapsed={true} startEmptyArrayClosed={true}
                startingValue={contributorshipSummaryState.style}
                setResourceState={setContributorshipSummaryState} />
        </div>
    }</>
}

const ContributionInstanceEntry = ({ elementName, startingValue, setResourceState }) => {

    const [contributionInstanceState, setContributionInstanceState] = useState({});

    useEffect(() => {
        let startingContributionInstance = {
            "type": "", "time": ""
        }
        if (startingValue) {
            if (startingValue.extension) { startingContributionInstance.extension = startingValue.extension; }
            if (startingValue.type) { startingContributionInstance.type = startingValue.type; }
            if (startingValue.time) { startingContributionInstance.time = startingValue.time; }
        }
        setContributionInstanceState(JSON.parse(JSON.stringify(startingContributionInstance)));
    }, []);

    useEffect((() => {
        if (Object.keys(contributionInstanceState).length) {
            let newInstance = {};
            if (contributionInstanceState.extension) { newInstance.extension = contributionInstanceState.extension; }
            if (contributionInstanceState.type) {
                newInstance.type = contributionInstanceState.type;
            }
            if (contributionInstanceState.time) {
                newInstance.time = contributionInstanceState.time;
            }
            if (Object.keys(newInstance).length === 0) {
                newInstance = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newInstance } });
        }
    }), [contributionInstanceState]);

    if (Object.keys(contributionInstanceState).length > 0) {
        return <div>
            <CodeableConceptEntry elementName='type'
                fieldLabel='Contribution Instance Type'
                startingValue={contributionInstanceState.type}
                valueSet={artifactContributionInstanceTypeValueSet}
                setResourceState={setContributionInstanceState} />
            <DateTimeEntry elementName='time' fieldLabel='Time'
                startingValue={contributionInstanceState.time}
                setResourceState={setContributionInstanceState} />
        </div>
    }
}

const CitationCitedArtifactContributorshipEntryEntry = ({
    elementName, startingValue, setResourceState, foi, containedResourceDictionary = {} }) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));

    const [contributorshipEntryState, setContributorshipEntryState] = useState({});

    useEffect(() => {
        let startingContributorshipEntry = {
            "contributor": "", "forenameInitials": "", "affiliation": [], "contributionType": [], "role": "",
            "contributionInstance": [], "correspondingContact": "", "rankingOrder": ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingContributorshipEntry.extension = startingValue.extension; }
            if (startingValue.contributor) { startingContributorshipEntry.contributor = startingValue.contributor; }
            if (startingValue.forenameInitials) { startingContributorshipEntry.forenameInitials = startingValue.forenameInitials; }
            if (startingValue.affiliation) { startingContributorshipEntry.affiliation = startingValue.affiliation; }
            if (startingValue.contributionType) { startingContributorshipEntry.contributionType = startingValue.contributionType; }
            if (startingValue.role) { startingContributorshipEntry.role = startingValue.role; }
            if (startingValue.contributionInstance) { startingContributorshipEntry.contributionInstance = startingValue.contributionInstance; }
            if (typeof startingValue.correspondingContact === "boolean") { startingContributorshipEntry.correspondingContact = startingValue.correspondingContact; }
            if (startingValue.rankingOrder) { startingContributorshipEntry.rankingOrder = startingValue.rankingOrder; }
        }
        setContributorshipEntryState(JSON.parse(JSON.stringify(startingContributorshipEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(contributorshipEntryState).length) {
            let newContributorship = {};
            if (contributorshipEntryState.extension) { newContributorship.extension = contributorshipEntryState.extension; }
            if (contributorshipEntryState.contributor) {
                newContributorship.contributor = contributorshipEntryState.contributor;
            }
            if (contributorshipEntryState.forenameInitials) {
                newContributorship.forenameInitials = contributorshipEntryState.forenameInitials;
            }
            if (contributorshipEntryState.affiliation !== null && contributorshipEntryState.affiliation !== undefined &&
                Array.isArray(contributorshipEntryState.affiliation) && contributorshipEntryState.affiliation.length !== 0) {
                newContributorship.affiliation = contributorshipEntryState.affiliation;
            }
            if (contributorshipEntryState.contributionType !== null && contributorshipEntryState.contributionType !== undefined &&
                Array.isArray(contributorshipEntryState.contributionType) && contributorshipEntryState.contributionType.length !== 0) {
                newContributorship.contributionType = contributorshipEntryState.contributionType;
            }
            if (contributorshipEntryState.role) {
                newContributorship.role = contributorshipEntryState.role;
            }
            if (contributorshipEntryState.contributionInstance !== null && contributorshipEntryState.contributionInstance !== undefined &&
                Array.isArray(contributorshipEntryState.contributionInstance) && contributorshipEntryState.contributionInstance.length !== 0) {
                newContributorship.contributionInstance = contributorshipEntryState.contributionInstance;
            }
            if (typeof contributorshipEntryState.correspondingContact === "boolean") {
                newContributorship.correspondingContact = contributorshipEntryState.correspondingContact;
            }
            if (contributorshipEntryState.rankingOrder) {
                newContributorship.rankingOrder = contributorshipEntryState.rankingOrder;
            }
            if (Object.keys(newContributorship).length === 0) {
                newContributorship = null;
            }
            setResourceState(prevState => {
                return { ...prevState, [elementName]: JSON.parse(JSON.stringify(newContributorship)) };
            });
        }
    }), [contributorshipEntryState]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipEntryElement(contributorshipEntryState, elementName,
                "contributor", "Reference",
                parentResourceDetail, dispatch);
        }
    }, [contributorshipEntryState.contributor]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipEntryElement(contributorshipEntryState, elementName,
                "forenameInitials", "string",
                parentResourceDetail, dispatch);
        }
    }, [contributorshipEntryState.forenameInitials]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipEntryElement(contributorshipEntryState, elementName,
                "affiliation", "ArrayOfReference",
                parentResourceDetail, dispatch);
        }
    }, [contributorshipEntryState.affiliation]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipEntryElement(contributorshipEntryState, elementName,
                "contributionType", "ArrayOfCodeableConcept",
                parentResourceDetail, dispatch);
        }
    }, [contributorshipEntryState.contributionType]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipEntryElement(contributorshipEntryState, elementName,
                "role", "CodeableConcept",
                parentResourceDetail, dispatch);
        }
    }, [contributorshipEntryState.role]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipEntryElement(contributorshipEntryState, elementName,
                "contributionInstance", "ArrayOfCitedArtifactContributionInstance",
                parentResourceDetail, dispatch);
        }
    }, [contributorshipEntryState.contributionInstance]);

    useEffect(() => {
        if (typeof contributorshipEntryState.correspondingContact === "boolean") {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipEntryElement(contributorshipEntryState, elementName,
                "correspondingContact", "boolean",
                parentResourceDetail, dispatch);
        }
    }, [contributorshipEntryState.correspondingContact]);

    useEffect(() => {
        if (foi) {//Note: elementName sent from ArrayEntry is index sent to updateChangeReport
            updateCitedArtifactContributorshipEntryElement(contributorshipEntryState, elementName,
                "rankingOrder", "positiveInt",
                parentResourceDetail, dispatch);
        }
    }, [contributorshipEntryState.rankingOrder]);

    return <>
        {contributorshipEntryState.affiliation !== undefined &&
            <div>
                <ReferenceEntry elementName='contributor' fieldLabel='Contributor'
                    startingValue={contributorshipEntryState.contributor}
                    containedResourceDictionary={containedResourceDictionary}
                    setResourceState={setContributorshipEntryState} enableCreation={true}
                    referencedResourceTypes={['Practitioner', 'Organization']}
                    startCollapsed={false} />
                <StringEntry elementName='forenameInitials' fieldLabel='Initials (for forename)'
                    startingValue={contributorshipEntryState.forenameInitials} setResourceState={setContributorshipEntryState} />
                <ArrayEntry datatype='Reference' elementName='affiliation' fieldLabel='Affiliation'
                    startingValue={contributorshipEntryState.affiliation}
                    setResourceState={setContributorshipEntryState} enableCreation={true}
                    referencedResourceTypes={['Organization', 'PractitionerRole']} startCollapsed={true} />
                <ArrayEntry datatype='CodeableConcept'
                    elementName='contributionType' fieldLabel='Contribution Type'
                    valueSet={citedArtifactContributionTypeValueSet}
                    startingValue={contributorshipEntryState.contributionType}
                    setResourceState={setContributorshipEntryState} />
                <CodeableConceptEntry elementName='role' fieldLabel='Contributior Role'
                    valueSet={contributorRoleValueSet}
                    startingValue={contributorshipEntryState.role}
                    setResourceState={setContributorshipEntryState} />
                <ArrayEntry datatype='ContributionInstance'
                    elementName='contributionInstance' fieldLabel='Contribution Instance'
                    startingValue={contributorshipEntryState.contributionInstance}
                    setResourceState={setContributorshipEntryState} />
                <BooleanEntry elementName='correspondingContact' fieldLabel='Corresponding Contact'
                    startingValue={contributorshipEntryState.correspondingContact}
                    setResourceState={setContributorshipEntryState} />
                <PositiveIntEntry elementName='rankingOrder'
                    fieldLabel='Ranking Order (for listing)' startingValue={contributorshipEntryState.rankingOrder}
                    setResourceState={setContributorshipEntryState} />
            </div>
        }
    </>
}

const CitedArtifactContributorshipEntry = ({ foi, startingValue, setCitedArtifactContributorshipState,
    containedResourceDictionary = {}
}) => {

    const dispatch = useDispatch();
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));

    const [contributorshipState, setContributorshipState] = useState({});

    useEffect(() => {
        let startingContributorship = {
            "complete": "", "entry": [], "summary": []
        };
        if (startingValue) {
            if (startingValue.extension) { startingContributorship.extension = startingValue.extension; }
            if (typeof startingValue.complete === "boolean") {
                startingContributorship.complete = startingValue.complete;
            }
            if (startingValue.entry) { startingContributorship.entry = startingValue.entry; }
            if (startingValue.summary) { startingContributorship.summary = startingValue.summary; }
        }
        setContributorshipState(JSON.parse(JSON.stringify(startingContributorship)));
    }, []);

    useEffect((() => {
        if (Object.keys(contributorshipState).length) {
            let newContributorship = {};
            if (contributorshipState.extension) { newContributorship.extension = contributorshipState.extension; }
            if (contributorshipState.complete === true || contributorshipState.complete === false) {
                newContributorship.complete = contributorshipState.complete;
            }
            if (contributorshipState.entry !== null && contributorshipState.entry !== undefined &&
                Array.isArray(contributorshipState.entry) && contributorshipState.entry.length !== 0) {
                newContributorship.entry = contributorshipState.entry;
            }
            if (contributorshipState.summary !== null && contributorshipState.summary !== undefined &&
                Array.isArray(contributorshipState.summary) && contributorshipState.summary.length !== 0) {
                newContributorship.summary = contributorshipState.summary;
            }
            //Previously it would put a contributorship element within contributorship
            setCitedArtifactContributorshipState(prevState => { return { ...prevState, ...JSON.parse(JSON.stringify(newContributorship)) }; });
            /*
            setCitedArtifactState(prevState => {
                console.log(JSON.stringify(prevState));
                console.log(JSON.stringify(prevState.contributorship));
                return { ...prevState, "contributorship": JSON.parse(JSON.stringify(newContributorship)) };
            });
            */
        }
    }), [contributorshipState]);

    useEffect(() => {
        if (typeof contributorshipState.complete === "boolean") {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousFinalValue = newResourceDetail.revisedResourceJson.citedArtifact?.contributorship?.complete ?? null;
            if (previousFinalValue === null ||
                previousFinalValue !== contributorshipState.complete) {
                if (!newResourceDetail.revisedResourceJson.citedArtifact.contributorship) {
                    newResourceDetail.revisedResourceJson.citedArtifact.contributorship = {};
                }
                newResourceDetail.revisedResourceJson.citedArtifact.contributorship.complete = contributorshipState.complete
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [contributorshipState.complete]);

    return <div>
        {contributorshipState.entry !== undefined && <>
            <BooleanEntry elementName="complete" fieldLabel="Contributor list complete?"
                startingValue={typeof contributorshipState.complete === "boolean" ?
                    contributorshipState.complete : null}
                setResourceState={setContributorshipState} storeFalse={true} />
            <ArrayEntry datatype='CitationCitedArtifactContributorshipSummary' elementName='summary'
                fieldLabel='Summary Statement' foi={foi}
                startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed
                startingValue={contributorshipState.summary} setResourceState={setContributorshipState} />
            <ArrayEntry datatype='CitationCitedArtifactContributorshipEntry' elementName='entry'
                fieldLabel='Individual Contributor' foi={foi}
                containedResourceDictionary={containedResourceDictionary}
                startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed
                startingValue={contributorshipState.entry} setResourceState={setContributorshipState} />
        </>}
    </div>
}

const ContributorshipIndividualsRowsDisplay = ({ contributorshipEntry, containedResourceDictionary = {} }) => {
    return <>
        <Table.Row>
            <Table.Cell style={{ backgroundColor: "#F6F6FF", minWidth: "140px", verticalAlign: "top" }}>
                <DisplayFromFHIRReference reference={contributorshipEntry.contributor}
                    containedResourceJson={contributorshipEntry.contributor?.reference && containedResourceDictionary[contributorshipEntry.contributor.reference]} />
            </Table.Cell>
            <Table.Cell style={{ backgroundColor: "#F6F6FF", paddingBottom: "0px", verticalAlign: "top" }}
                colSpan="3">
                {contributorshipEntry.forenameInitials &&
                    <>
                        <b>Initials (for forename): </b>{contributorshipEntry.forenameInitials}
                    </>
                }
            </Table.Cell>
        </Table.Row>
        {(contributorshipEntry.contributor?.identifier || contributorshipEntry.contributor?.reference) &&
            <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell><b>Identifier</b></Table.Cell>
                <Table.Cell>
                    {contributorshipEntry.contributor.identifier ?
                        <>{displayStringFromFHIRIdentifier(contributorshipEntry.contributor.identifier)}</>
                        :
                        <>{contributorshipEntry.contributor.reference}</>
                    }
                </Table.Cell>
            </Table.Row>
        }
        {contributorshipEntry.affiliation?.length > 0 &&
            <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell><b>Affiliation</b></Table.Cell>
                <Table.Cell colSpan="2">
                    {contributorshipEntry.affiliation.map((affiliation, affiliationIndex) => {
                        return <div key={affiliationIndex}>
                            <DisplayFromFHIRReference reference={affiliation}
                                containedResourceJson={affiliation?.reference && containedResourceDictionary[affiliation.reference]} />
                            <br />
                        </div>;
                    })}

                </Table.Cell>
            </Table.Row>
        }
        {(contributorshipEntry.role || contributorshipEntry.correspondingContact) &&
            <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell><b>Role</b></Table.Cell>
                <Table.Cell colSpan="2">
                    <>
                        {contributorshipEntry.correspondingContact && <>Corresponding </>}
                        {contributorshipEntry.role && simpleDisplayStringFromFHIRCodeableConcept(contributorshipEntry.role)}
                    </>
                </Table.Cell>
            </Table.Row>
        }
        {contributorshipEntry.contributionType?.length > 0 &&
            <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell><b>Contribution types</b></Table.Cell>
                <Table.Cell colSpan="2">
                    <>{getCodeableConceptDisplayForObjectOrArray(contributorshipEntry.contributionType)}</>
                </Table.Cell>
            </Table.Row>
        }
        {contributorshipEntry.contributionInstance?.length > 0 &&
            <Table.Row>
                <Table.Cell></Table.Cell>
                <Table.Cell><b>Contribution instances</b></Table.Cell>
                <Table.Cell colSpan="2">
                    {contributorshipEntry.contributionInstance.map((contributionInstance, contributionInstanceIndex) => {
                        return <span key={contributionInstanceIndex}>
                            <>{simpleDisplayStringFromFHIRCodeableConcept(contributionInstance.type)} {getDateTimeDisplay(contributionInstance.time)}<br /></>
                        </span>
                    })}
                </Table.Cell>
            </Table.Row>
        }
    </>;
}

const CitedArtifactContributorshipDisplay = ({ contributorship, containedResourceDictionary = {} }) => {
    let contributorshipSummaries = contributorship?.summary;
    let numberOfIndividuals = contributorship?.entry?.length;

    return <div>{contributorship ? <>
        <div className={`CitationContributorshipSubSection`} style={{ paddingLeft: "40px" }}>
            <span>
                <b style={{ fontSize: "1.1em" }}>{contributorshipSummaries?.length !== 1 ?
                    <>Summary statements</> : <>Summary statement</>}:</b>
            </span>
            <div className={`CitationContributorshipSubSection`} style={{ paddingLeft: "40px" }}>
                {contributorshipSummaries?.length >= 1 ?
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Summary</Table.HeaderCell>
                                <Table.HeaderCell>Type</Table.HeaderCell>
                                <Table.HeaderCell>Source</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {contributorshipSummaries.map((summaryEntry, index) => {
                                return <Table.Row key={index}>
                                    <Table.Cell style={{ verticalAlign: "top" }}>
                                        <DisplayFromFHIRMarkdown markdown={summaryEntry.value} />
                                    </Table.Cell>
                                    <Table.Cell style={{ verticalAlign: "top", maxWidth: "260px" }}>
                                        <>{simpleDisplayStringFromFHIRCodeableConcept(summaryEntry.type)}</>
                                    </Table.Cell>
                                    <Table.Cell style={{ verticalAlign: "top" }}>
                                        <>{simpleDisplayStringFromFHIRCodeableConcept(summaryEntry.source)}</>
                                    </Table.Cell>
                                </Table.Row>;
                            })
                            }
                        </Table.Body>
                    </Table>
                    :
                    <span style={{ color: "#EE1111" }}>No data.</span>
                }
            </div>
            <span>
                <b style={{ fontSize: "1.1em" }}>{numberOfIndividuals === 1 ?  //integer integer comparison
                    <>Individual contributor</> : <>Individual contributors</>}:
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    Contributor list complete: </b>
                <>{contributorship.complete ? <>yes</> : <>no</>}</>
            </span>
            <div className={`CitationContributorshipSubSection`} style={{ paddingLeft: "40px" }}>
                {numberOfIndividuals >= 1 ?  //integer integer comparison
                    <Table style={{ borderLeft: "solid 2px #777777" }}>
                        {contributorship.entry.map((contributorshipEntry, contributorshipEntryIndex) => {
                            return <Table.Body key={contributorshipEntryIndex}>
                                <ContributorshipIndividualsRowsDisplay
                                    contributorshipEntry={contributorshipEntry}
                                    containedResourceDictionary={containedResourceDictionary} />
                            </Table.Body>
                        })
                        }
                    </Table>
                    :
                    <span style={{ color: "#EE1111" }}>No data.</span>
                }
            </div>
        </div>
    </>
        :
        <><span style={{ color: "#EE1111" }}>No data.</span></>}
    </div>

}

const createMetaTagFromClassificationArtifactAssessmentJson = (metaTag, json, meshHeading) => {
    let classificationJson = JSON.parse(JSON.stringify(json));
    if (metaTag["display"] === "" && classificationJson.title) {
        metaTag["display"] = classificationJson.title;
    }
    metaTag["copyright"] = classificationJson.copyright || "";
    for (let classificationContentIndex in classificationJson.content) {
        let classificationBit = classificationJson.content[classificationContentIndex];
        let metaTagContent = { "classifiersDisplayString": "", "qualifiersDisplay": "", "freeToShare": classificationBit.freeToShare, "authorDisplay": "" };  //true, false, or undefined
        if (classificationBit.type) {
            if (classificationBit.type.coding?.length > 0 && classificationBit.type.coding[0].display) {
                metaTagContent["typeDisplay"] = classificationBit.type.coding[0].display;
            } else if (classificationBit.type.text) {
                metaTagContent["typeDisplay"] = classificationBit.type.text;
            } else if (classificationBit.type.coding?.length > 0 && classificationBit.type.coding[0].code) {
                metaTagContent["typeDisplay"] = classificationBit.type.coding[0].code;
            }
            if (metaTagContent["typeDisplay"].toLowerCase().replace(" ", "-") === "mesh-heading") {
                meshHeading = true;
            }
        }
        if (classificationBit.author) {
            if (classificationBit.author.display) {
                metaTagContent["authorDisplay"] = classificationBit.author.display;
            } else if (classificationBit.author.reference) {
                metaTagContent["authorDisplay"] = classificationBit.author.reference + " (This will be replaced with the author display)";
            }
        }

        metaTagContent["classifiersDisplayString"] = getCodeableConceptDisplayForObjectOrArray(classificationBit.classifier);
        let qualifierDisplayArray = [];
        for (let componentIndex in classificationBit.component) {
            let component = classificationBit.component[componentIndex];
            if (component.type?.text.toLowerCase() === "qualifier") {
                let qualifierString = "";
                if (component.classifier) {
                    qualifierString = getCodeableConceptDisplayForObjectOrArray(component.classifier);
                }
                for (let subComponentIndex in component.component) {
                    let subComponent = component.component[subComponentIndex];
                    if (subComponent.type.text) {
                        for (let subComponentClassifierIndex in subComponent.classifier) {
                            let subComponentClassifier = subComponent.classifier[subComponentClassifierIndex];
                            for (let subComponentClassifierCodingIndex in subComponentClassifier.coding) {
                                let subComponentClassifierCoding = subComponentClassifier.coding[subComponentClassifierCodingIndex];
                                let code = subComponentClassifierCoding.code;
                                if (code.toUpperCase() === "Y" || code.toLowerCase() === "yes" ||
                                    code.toLowerCase() === "is-major-topic" || code.toLowerCase() === "ismajortopic" ||
                                    code === "C49488") {
                                    qualifierString += ` (${subComponent.type.text})`;
                                }
                            }
                        }
                    }
                }
                if (qualifierString) {
                    qualifierDisplayArray.push(qualifierString);
                }
            }
        }
        metaTagContent["qualifiersDisplay"] = qualifierDisplayArray.join("; ");

        metaTag["contents"].push(metaTagContent);
    }
    return [metaTag, meshHeading];
}

const CitedArtifactPartEntry = ({ startingValue, setCitedArtifactState }) => {

    const [partState, setPartState] = useState({});

    useEffect(() => {
        let startingPart = {
            "type": "", "value": ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingPart.extension = startingValue.extension; }
            if (startingValue.type) { startingPart.type = startingValue.type; }
            if (startingValue.value) { startingPart.value = startingValue.value; }
        }
        setPartState(JSON.parse(JSON.stringify(startingPart)));
    }, []);

    useEffect((() => {
        if (Object.keys(partState).length) {
            let newPart = {};
            if (partState.extension) { newPart.extension = partState.extension; }
            if (partState.type && Object.keys(partState.type).length > 0) { newPart.type = partState.type; }
            if (partState.value) { newPart.value = partState.value; }
            if (Object.keys(newPart).length === 0) {
                newPart = null;
            }
            setCitedArtifactState(prevState => { return { ...prevState, "part": newPart } });
        }
    }), [partState]);

    if (Object.keys(partState).length > 0) {
        return <div>
            <CodeableConceptEntry elementName='type' fieldLabel='Part Type'
                codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet("citedArtifactPartTypeValueSet")}
                startingValue={partState.type}
                setResourceState={setPartState} />
            <StringEntry elementName='value' fieldLabel='Part Value'
                startingValue={partState.value} setResourceState={setPartState} />
        </div>
    }
}

const CitationDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));
    let containedResources = {};
    if (resourceJson.contained) {
        for (const containedResource of resourceJson.contained) {
            containedResources[containedResource.id] = containedResource;
        }
    }
    let isJournalArticleCitationProfile;
    let isBookCitationProfile;
    let isBookPartCitationProfile;
    let isDatabaseCitationProfile;
    let isDatabaseEntryCitationProfile;
    let isDatasetCitationProfile;
    let isPreprintCitationProfile;
    let isSoftwareCitationProfile;
    let isWebPageCitationProfile;
    if (resourceJson.meta?.profile?.length) {
        for (const profile of resourceJson.meta.profile) {
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/journal-article-citation") {
                isJournalArticleCitationProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/book-citation") {
                isBookCitationProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/book-part-citation") {
                isBookPartCitationProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/database-citation") {
                isDatabaseCitationProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/database-entry-citation") {
                isDatabaseEntryCitationProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/dataset-citation") {
                isDatasetCitationProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/preprint-citation") {
                isPreprintCitationProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/software-citation") {
                isSoftwareCitationProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/web-page-citation") {
                isWebPageCitationProfile = true;
            }
        }
    }

    const [citationResourceState, setCitationResourceState] = useState({});
    const [selectedCitationSummaryState, setSelectedCitationSummaryState] = useState(0);
    const [citedArtifactState, setCitedArtifactState] = useState({});
    const [citedArtifactContributorshipState, setCitedArtifactContributorshipState] = useState({});
    const [titleTypeOptions, setTitleTypeOptions] = useState({ "options": generateValueSetOptions("titleTypeValueSet") });
    const [abstractTypeOptions, setAbstractOptions] = useState({ "options": generateValueSetOptions("citedArtifactAbstractTypeValueSet") });
    const [publicationFormArrayState, setPublicationFormArrayState] = useState([]);
    const [articleUrlTypeOptions, setArticleUrlTypeOptions] = useState({ "options": generateValueSetOptions("artifactUrlClassifierValueSet") });
    const [citedArtifactStatusTypeOptions, setCitedArtifactStatusTypeOptions] = useState({ "options": generateValueSetOptions("citedArtifactStatusTypeValueSet") });
    const [artifactClassifierTypeOptions, setArtifactClassifierTypeOptions] = useState({ "options": generateValueSetOptions("citationArtifactClassifierValueSet") });
    const [artifactRelationshipTypeOptions, setArtifactRelationshipTypeOptions] = useState({ "options": generateValueSetOptions("artifactRelationshipTypeValueSet") });
    const [citedArtifactClassificationsState, setCitedArtifactClassificationsState] = useState({
        "simpleClassifications": [], "complexClassifications": []
    });
    const [classificationTypeOptions, setClassificationTypeOptions] = useState({ "options": generateValueSetOptions("citedArtifactClassificationTypeValueSet") });
    const [classifierOptions, setClassifierOptions] = useState({ "options": generateValueSetOptions("citationArtifactClassifierValueSet") });
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    const updateCitationSummary = () => {
        setCitationResourceState(prevState => {
            let computablePublishingSummary = generateComputablePublishingSummary(prevState);
            let computablePublishingSummarySliceFound;
            let summaryArray = prevState.summary ? JSON.parse(JSON.stringify(prevState.summary)) : [];
            for (let item of summaryArray) {
                if (item?.style?.coding?.[0]?.code === "comppub") {
                    computablePublishingSummarySliceFound = true;
                    item.text = computablePublishingSummary;
                }
            }
            if (computablePublishingSummarySliceFound !== true) {
                summaryArray.push({
                    "style": {
                        "coding": [
                            {
                                "system": "http://terminology.hl7.org/CodeSystem/citation-summary-style",
                                "code": "comppub",
                                "display": "Computable Publishing"
                            }
                        ]
                    },
                    "text": computablePublishingSummary
                })
            }
            if (summaryArray.length) {
                return { ...prevState, "summary": summaryArray };
            } else {
                return prevState;
            }
        });
    }

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Citation Summary", "Identifiers", "Title", "Abstract",
            "Publication Form", "URLs", "Contributors", "Classifiers", "MeSH Headings",
            "Status", "Modifiers", "Related Articles"]));
        dispatch(changeBottomNavigationElements(["Citation Metadata", "Associated Resources", "JSON Outline"]));
        dispatch(changeBrowserTabTitle("FEvIR Citation - " + displayTitle));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setCitationResourceState(olderVersionJson);
                setCitedArtifactState(olderVersionJson.citedArtifact ? JSON.parse(JSON.stringify(olderVersionJson.citedArtifact)) : {});
                setPublicationFormArrayState(olderVersionJson.citedArtifact?.publicationForm ? JSON.parse(JSON.stringify(olderVersionJson.citedArtifact?.publicationForm)) : []);
                setCitedArtifactContributorshipState(olderVersionJson.citedArtifact?.contributorship ? JSON.parse(JSON.stringify(olderVersionJson.citedArtifact?.contributorship)) : {});
                if (olderVersionJson.citedArtifact?.classification?.length) {
                    let simpleClassifications = [];
                    let complexClassifications = [];
                    for (const classification of olderVersionJson.citedArtifact.classification) {
                        if (classification.artifactAssessment) {
                            complexClassifications.push(classification);
                        } else {
                            simpleClassifications.push(classification);
                        }
                    }
                    setCitedArtifactClassificationsState({
                        "simpleClassifications": JSON.parse(JSON.stringify(simpleClassifications)), "complexClassifications": JSON.parse(JSON.stringify(complexClassifications))
                    })
                }
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
            setCitationResourceState(resourceJson);
            setCitedArtifactState(resourceJson.citedArtifact ? JSON.parse(JSON.stringify(resourceJson.citedArtifact)) : {});
            setPublicationFormArrayState(resourceJson.citedArtifact?.publicationForm ? JSON.parse(JSON.stringify(resourceJson.citedArtifact?.publicationForm)) : []);
            setCitedArtifactContributorshipState(resourceJson.citedArtifact?.contributorship ? JSON.parse(JSON.stringify(resourceJson.citedArtifact?.contributorship)) : {});
            if (resourceJson.citedArtifact?.classification?.length) {
                let simpleClassifications = [];
                let complexClassifications = [];
                for (const classification of resourceJson.citedArtifact.classification) {
                    if (classification.artifactAssessment) {
                        complexClassifications.push(classification);
                    } else {
                        simpleClassifications.push(classification);
                    }
                }
                setCitedArtifactClassificationsState({
                    "simpleClassifications": JSON.parse(JSON.stringify(simpleClassifications)), "complexClassifications": JSON.parse(JSON.stringify(complexClassifications))
                })
            }
        }
    }, []);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: Citation " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect(() => {
        if (!jsonEquals(publicationFormArrayState, citedArtifactState?.publicationForm)) {
            setCitedArtifactState(prevState => { return { ...prevState, "publicationForm": JSON.parse(JSON.stringify(publicationFormArrayState)) }; });
        }
    }, [publicationFormArrayState]);

    useEffect(() => {
        if (!jsonEquals(citedArtifactContributorshipState, citedArtifactState?.contributorship)) {
            setCitedArtifactState(prevState => {
                return { ...prevState, "contributorship": citedArtifactContributorshipState ? JSON.parse(JSON.stringify(citedArtifactContributorshipState)) : {} };
            });
        }
    }, [citedArtifactContributorshipState]);

    useEffect(() => {
        let combinedClassifications = citedArtifactClassificationsState.simpleClassifications.concat(citedArtifactClassificationsState.complexClassifications);
        if (!jsonEquals(combinedClassifications, citedArtifactState?.classification)) {
            setCitedArtifactState(prevState => { return { ...prevState, "classification": combinedClassifications }; });
        }
    }, [citedArtifactClassificationsState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            const previousFinalValue = parentResourceDetail.revisedResourceJson.summary;
            if (citationResourceState.summary?.length > 0
                && !jsonEquals(previousFinalValue, citationResourceState.summary)
            ) {
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                newResourceDetail.revisedResourceJson.summary = JSON.parse(JSON.stringify(citationResourceState.summary));
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [citationResourceState.summary]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(citationResourceState, "classification", "ArrayOfCitationClassification", parentResourceDetail,
                dispatch);
        }
    }, [citationResourceState.classification]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(citationResourceState, "statusDate", "ArrayOfCitationStatusDate", parentResourceDetail,
                dispatch);
        }
    }, [citationResourceState.statusDate]);

    useEffect(() => {
        if (citedArtifactState && Object.keys(citedArtifactState).length) {
            setCitationResourceState(prevState => {
                return {
                    ...prevState,
                    "citedArtifact": JSON.parse(JSON.stringify(citedArtifactState))
                }
            })
        }
    }, [citedArtifactState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "identifier", "ArrayOfIdentifier", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.identifier]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "relatedIdentifier", "ArrayOfIdentifier", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.relatedIdentifier]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "title", "ArrayOfCitedArtifactTitle", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "abstract", "ArrayOfCitedArtifactAbstract", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.abstract]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "webLocation", "ArrayOfCitedArtifactWebLocation", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.webLocation]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "currentState", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.currentState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "statusDate", "ArrayOfCitedArtifactStatusDate", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.statusDate]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "dateAccessed", "dateTime", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.dateAccessed]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "version", "string", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.version]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "part", "CitedArtifactPart", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.part]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "baseCitation", "Reference", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.baseCitation]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "note", "ArrayOfAnnotation", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.note]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "relatesTo", "ArrayOfCitedArtifactRelatesTo", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.relatesTo]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateCitedArtifactElement(citedArtifactState, "classification", "ArrayOfCitedArtifactClassification", parentResourceDetail,
                dispatch);
        }
    }, [citedArtifactState?.classification]);

    if (!resourceJson) {
        return <Spinner />;
    }

    if (!citationResourceState) {
        return <h2>Citation Resource JSON is loading</h2>
    }

    if (!citedArtifactState) {
        return <h2>Citation JSON missing citedArtifact. (or it is loading)</h2>
    }

    return <div>
        {citationResourceState.id && <>
            <h3 id="citation-summary">Citation Summary</h3>
            <div style={{ paddingLeft: "40px" }}>
                <>{editMode ?
                    <>
                        <ArrayEntry datatype="CitationSummary" elementName="summary"
                            fieldLabel="Citation Summary" deletable
                            startingValue={citationResourceState.summary}
                            setResourceState={setCitationResourceState} />
                        <br /><br />
                        <span>
                            <span>   Save All Changes to refresh the display.</span>
                        </span>
                        <Button className="formButton" style={{ color: "#000000" }}
                            content={(citationResourceState.summary && Array.isArray(citationResourceState.summary)) ?
                                "⟳ Refresh Generated Summaries" : "Generate Computable Publishing Summary"}
                            onClick={() => {
                                updateCitationSummary();
                            }} />
                    </>
                    :
                    <>
                        {citationResourceState.summary &&
                            <>
                                <span><b style={{ fontSize: "1.1em" }}>Style: </b>
                                    {citationResourceState.summary.length === 1 ?
                                        <>{citationResourceState.summary[0].style?.coding?.[0]?.display || citationResourceState.summary[0].style?.text ||
                                            citationResourceState.summary[0].style?.coding?.[0]?.code || "Unclassified"}</>
                                        :
                                        <Select
                                            className="thinSelector"
                                            labelId="citation-summary-style-label"
                                            id="citation-summary-style"
                                            value={selectedCitationSummaryState}
                                            onChange={(e) => { setSelectedCitationSummaryState(e.target.value) }}
                                            disabled={!(citationResourceState.summary?.length >= 2)}
                                        >
                                            {citationResourceState.summary?.map((summary, index) => {
                                                return <MenuItem value={index} key={index}>
                                                    {summary.style?.coding?.[0]?.display || summary.style?.text ||
                                                        summary.style?.coding?.[0]?.code || "Unclassified"}
                                                </MenuItem>
                                            })}
                                        </Select>
                                    }
                                </span>
                                <br />
                                <span><b style={{ fontSize: "1.1em" }}>Summary: </b>
                                    {citationResourceState.summary?.length > 0 &&
                                        <DisplayFromFHIRMarkdown markdown={citationResourceState.summary[selectedCitationSummaryState]?.text} />
                                    }
                                </span>
                            </>
                        }
                    </>
                }</>
            </div>
            <h3 id="identifiers">{citedArtifactState?.identifier?.length === 1 ? <>Identifier</> : <>Identifiers</>}</h3>
            <div className={`CitationIdentifiersSubSection`} style={{ paddingLeft: "40px" }}>
                {citedArtifactState?.identifier?.length > 0 || citedArtifactState?.relatedIdentifier?.length > 0 ?
                    <>
                        {generateIdentifierTable(citedArtifactState.identifier, "identifiers",
                            editMode, identifierSystemLookup,
                            identifierDisplayLookup, startingIdentifierTypeOptions, setCitedArtifactState, "identifier")}
                        <h4 style={{ marginBottom: "0px" }}>
                            {citedArtifactState.relatedIdentifier?.length === 1 ? <>Related Identifier</> : <>Related Identifiers</>}
                            <span style={{ fontWeight: "normal" }}> (not unique to the cited article)</span>
                        </h4>
                        {generateIdentifierTable(citedArtifactState.relatedIdentifier, "relatedIdentifiers",
                            editMode, identifierSystemLookup,
                            identifierDisplayLookup, startingIdentifierTypeOptions, setCitedArtifactState, "relatedIdentifier")}
                    </>
                    :
                    <span style={{ color: "#EE1111" }}>No data.
                        <span>
                            {editMode &&
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="+ Add Identifier"
                                    onClick={() => {
                                        setCitedArtifactState(prevState => {
                                            return { ...prevState, "identifier": [{}] };
                                        });
                                    }} />}
                        </span>  <span>
                            {editMode &&
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="+ Add Related Identifier"
                                    onClick={() => {
                                        setCitedArtifactState(prevState => {
                                            return { ...prevState, "relatedIdentifier": [{}] };
                                        })
                                    }} />}
                        </span>
                    </span>
                }
            </div>
            <h3 id="title">{citedArtifactState?.title?.length <= 1 ? <>Title</> : <>Titles</>}</h3>
            <div className={`CitationTitlesSubSection`} style={{ paddingLeft: "40px" }}>
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Title text</Table.HeaderCell>
                            <Table.HeaderCell>Type</Table.HeaderCell>
                            <Table.HeaderCell>Language</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {citedArtifactState?.title?.map((titleX, index) => {
                            let title = titleX;
                            if (editMode) {
                                title = JSON.parse(JSON.stringify(titleX));
                                if (Array.isArray(title.type)) {
                                    for (let type of title.type) {
                                        if (typeof type === 'object' && !Array.isArray(type) && Array.isArray(type.coding)) {
                                            for (let coding of type.coding) {
                                                if (coding.system === "http://hl7.org/fhir/title-type") {
                                                    coding.system = "http://terminology.hl7.org/CodeSystem/title-type"
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            return <Table.Row key={index}>
                                {editMode ?
                                    <>
                                        <Table.Cell style={{ verticalAlign: "top" }}>
                                            <TextField style={{ width: "100%" }} multiline
                                                className="inputField" type='text'
                                                label={'Citation Title Text'} size="small" variant='outlined'
                                                value={title.text}
                                                onChange={(e) => {
                                                    setCitedArtifactState(prevState => {
                                                        let newTitleArray = JSON.parse(JSON.stringify(prevState.title));
                                                        newTitleArray[index]["text"] = e.target.value;
                                                        return { ...prevState, "title": newTitleArray };
                                                    })
                                                }} />
                                        </Table.Cell>
                                        <Table.Cell style={{ verticalAlign: "top" }}>
                                            <DropDownWithAdditions currentValue={title.type || ""}
                                                labelText={'Pick Title Type'}
                                                options={titleTypeOptions.options}
                                                optionsLookup={generateValueSetLookup("titleTypeValueSet")}
                                                setOptions={setTitleTypeOptions}
                                                setResourceElementStateForArray={setCitedArtifactState}
                                                elementName={"title"} subelementName={"type"} index={index}
                                                multiple={true}
                                            />
                                            {(title.type?.length === 1 && titleTypeLookup[title.type.coding?.[0]?.code]) &&
                                                <p><b>Tool tip:</b> used for {titleTypeLookup[title.type.coding[0].code].definition}</p>
                                            }
                                            {title.type?.length > 1 &&
                                                <p><b>Tool tip:</b> you have two or more title types, you can remove any title type that you no longer wish to keep.</p>
                                            }
                                        </Table.Cell>
                                        <Table.Cell style={{ verticalAlign: "top" }}>
                                            <Dropdown placeholder={'Pick Language'}
                                                closeOnChange search selection
                                                selectOnBlur={false} multiple={false} clearable
                                                style={{ width: "50px" }}
                                                options={languageCodeOptions}
                                                value={typeof title.language === "string" ?
                                                    title.language : title.language?.coding?.[0]?.code ?
                                                        title.language.coding[0].code : title.language?.text ?
                                                            title.language.text : ""}
                                                onChange={(e, data) => {
                                                    setCitedArtifactState(prevState => {
                                                        let newTitleArray = JSON.parse(JSON.stringify(prevState.title));
                                                        newTitleArray[index]["language"] = data.value;
                                                        return { ...prevState, "title": newTitleArray };
                                                    });
                                                }}
                                            />
                                        </Table.Cell>
                                    </>
                                    :
                                    <>
                                        <Table.Cell style={{ verticalAlign: "top" }}>
                                            <DisplayFromFHIRMarkdown markdown={title.text} />
                                        </Table.Cell>
                                        <Table.Cell style={{ verticalAlign: "top" }}>
                                            <>{getCodeableConceptDisplayForObjectOrArray(title.type)}</>
                                        </Table.Cell>
                                        <Table.Cell style={{ verticalAlign: "top" }}>
                                            <>
                                                {typeof title.language === "object" ?
                                                    <>{(languageCodeDisplay[title.coding?.[0]?.code])
                                                        ?
                                                        <>{" " + languageCodeDisplay[title.coding[0].code]}</>
                                                        :
                                                        <>Unrecognized language: {title.language.text || title.language.coding?.[0]?.display || title.language.coding?.[0]?.code || title.language.toString()}</>
                                                    }</>
                                                    :
                                                    <>{(languageCodeDisplay[title.language])
                                                        ?
                                                        <>{" " + languageCodeDisplay[title.language]}</>
                                                        :
                                                        <>Unrecognized language: {title.language}</>
                                                    }</>
                                                }
                                            </>
                                        </Table.Cell>
                                    </>
                                }
                            </Table.Row>;
                        })
                        }
                    </Table.Body>
                </Table>
                <span>
                    {editMode &&
                        <Button className="formButton" style={{ color: "#000000" }}
                            content="+ Add Title"
                            onClick={() => {
                                setCitedArtifactState(prevState => {
                                    let newArray = [{}];
                                    if (prevState["title"]) {
                                        newArray = prevState["title"].concat([{}]);
                                    }
                                    return { ...prevState, "title": newArray };
                                });
                            }}
                        />}
                </span>
            </div>
            <h3 id="abstract">
                {citedArtifactState?.abstract?.length <= 1 ? <>Abstract</> : <>Abstracts</>}
            </h3>
            <div className={`CitationAbstractsSubSection`} style={{ paddingLeft: "40px" }}>
                {citedArtifactState?.abstract?.length >= 1 ?
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell style={{ minWidth: "440px" }}>Abstract text</Table.HeaderCell>
                                <Table.HeaderCell>Type</Table.HeaderCell>
                                <Table.HeaderCell>Language</Table.HeaderCell>
                                <Table.HeaderCell>Abstract copyright</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {citedArtifactState.abstract.map((abstractX, index) => {
                                let abstract = abstractX;
                                if (editMode) {
                                    abstract = JSON.parse(JSON.stringify(abstractX));
                                    if (Array.isArray(abstract.type)) {
                                        for (let type of abstract.type) {
                                            if (typeof type === 'object' && !Array.isArray(type) && Array.isArray(type.coding)) {
                                                for (let coding of type.coding) {
                                                    if (coding.system === "http://hl7.org/fhir/cited-artifact-abstract-type") {
                                                        coding.system = "http://terminology.hl7.org/CodeSystem/cited-artifact-abstract-type"
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                return <Table.Row key={index}>
                                    {editMode ?
                                        <>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <TextField style={{ width: "100%" }} multiline
                                                    className="inputField" type='text'
                                                    label={'Citation Abstract Text'} size="small"
                                                    variant='outlined' value={abstract.text}
                                                    onChange={(e) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newAbstractArray = JSON.parse(JSON.stringify(prevState.abstract));
                                                            newAbstractArray[index]["text"] = e.target.value;
                                                            return { ...prevState, "abstract": newAbstractArray };
                                                        })
                                                    }} />
                                            </Table.Cell>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <DropDownWithAdditions currentValue={abstract.type || []}
                                                    labelText={'Pick Abstract Type'}
                                                    options={abstractTypeOptions.options}
                                                    optionsLookup={generateValueSetLookup("citedArtifactAbstractTypeValueSet")}
                                                    setOptions={setAbstractOptions}
                                                    setResourceElementStateForArray={setCitedArtifactState}
                                                    index={index}
                                                    elementName={"abstract"} subelementName={"type"}
                                                    multiple={true} />
                                                {(abstract.type?.length === 1 && abstractTypeLookup[abstract.type.coding?.[0]?.code]) &&
                                                    <p><b>Tool tip:</b> used for {abstractTypeLookup[abstract.type.coding[0].code].definition}</p>}
                                                {abstract.type?.length > 1 &&
                                                    <p><b>Tool tip:</b> you have two or more abstract types, you can remove any abstract type that you no longer wish to keep.</p>}
                                            </Table.Cell>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <Dropdown placeholder={'Pick Language'}
                                                    closeOnChange search selection
                                                    selectOnBlur={false} multiple={false} clearable
                                                    style={{ width: "50px" }}
                                                    options={languageCodeOptions}
                                                    value={abstract.language || ""}
                                                    onChange={(e, data) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newAbstractArray = JSON.parse(JSON.stringify(prevState.abstract));
                                                            newAbstractArray[index]["language"] = data.value;
                                                            return { ...prevState, "abstract": newAbstractArray };
                                                        });
                                                    }}
                                                />
                                            </Table.Cell>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <TextField style={{ width: "100%" }} multiline
                                                    className="inputField" type='text'
                                                    label={'Abstract Copyright'} size="small" variant='outlined'
                                                    value={abstract.copyright}
                                                    onChange={(e) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newAbstractArray = JSON.parse(JSON.stringify(prevState.abstract));
                                                            newAbstractArray[index]["copyright"] = e.target.value;
                                                            return { ...prevState, "abstract": newAbstractArray };
                                                        })
                                                    }} />
                                            </Table.Cell>
                                        </>
                                        :
                                        <>
                                            <Table.Cell style={{ verticalAlign: "top", whiteSpace: "pre-wrap" }}>
                                                <DisplayFromFHIRMarkdown markdown={abstract.text} />
                                            </Table.Cell>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <>{getCodeableConceptDisplayForObjectOrArray(abstract.type)}</>
                                            </Table.Cell>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <>
                                                    {typeof abstract.language === "object" ?
                                                        <>{(languageCodeDisplay[abstract.language.coding?.[0]?.code])
                                                            ?
                                                            <>{" " + languageCodeDisplay[abstract.language.coding[0].code]}</>
                                                            :
                                                            <>Unrecognized language: {abstract.language.text || abstract.language.coding?.[0]?.display || abstract.language.coding?.[0]?.code || abstract.language.toString()}</>
                                                        }</>
                                                        :
                                                        <>{(languageCodeDisplay[abstract.language])
                                                            ?
                                                            <>{" " + languageCodeDisplay[abstract.language]}</>
                                                            :
                                                            <>Unrecognized language: {abstract.language}</>
                                                        }</>
                                                    }
                                                </>
                                            </Table.Cell>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <DisplayFromFHIRMarkdown markdown={abstract.copyright} />
                                            </Table.Cell>
                                        </>
                                    }
                                </Table.Row>;
                            })
                            }
                        </Table.Body>
                    </Table>
                    :
                    <span style={{ color: "#EE1111" }}>No data.</span>
                }
                <span>
                    {editMode &&
                        <Button className="formButton" style={{ color: "#000000" }}
                            content="+ Add Abstract"
                            onClick={() => {
                                setCitedArtifactState(prevState => {
                                    let newArray = [{}];
                                    if (prevState["abstract"]) {
                                        newArray = prevState["abstract"].concat([{}]);
                                    }
                                    return { ...prevState, "abstract": newArray };
                                });
                            }} />}
                </span>
            </div>
            <br />
            <div id="publication-form">
                {
                    editMode ?
                        <CitedArtifactPublicationFormArrayEntry startingValue={publicationFormArrayState}
                            setPublicationFormArrayState={setPublicationFormArrayState}
                            containedResourceDictionary={containedResourceDictionary}
                            isJournalArticleCitationProfile={isJournalArticleCitationProfile}
                            isBookCitationProfile={isBookCitationProfile}
                            isBookPartCitationProfile={isBookPartCitationProfile}
                            isDatabaseCitationProfile={isDatabaseCitationProfile}
                            isDatabaseEntryCitationProfile={isDatabaseEntryCitationProfile}
                            isDatasetCitationProfile={isDatasetCitationProfile}
                            isPreprintCitationProfile={isPreprintCitationProfile}
                            isSoftwareCitationProfile={isSoftwareCitationProfile}
                            isWebPageCitationProfile={isWebPageCitationProfile}
                            foi={foi} />
                        :
                        <CitedArtifactPublicationFormDisplay publicationForms={citationResourceState.citedArtifact?.publicationForm}
                            containedResourceDictionary={containedResourceDictionary} />
                }
            </div>
            <h3 id="urls">{citedArtifactState?.webLocation?.length === 1 ? <>URL</> : <>URLs</>}</h3>
            <div className={`CitationWebLocationSubSection`} style={{ paddingLeft: "40px" }}>
                {citedArtifactState?.webLocation?.length >= 1 ?
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>URL</Table.HeaderCell>
                                <Table.HeaderCell style={{ width: "50%" }}>Type</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {citedArtifactState.webLocation.map((webLocationX, index) => {
                                let webLocation = webLocationX;
                                if (editMode) {
                                    webLocation = JSON.parse(JSON.stringify(webLocationX));
                                    if (Array.isArray(webLocation.classifier)) {
                                        for (let classifier of webLocation.classifier) {
                                            if (Array.isArray(classifier.coding)) {
                                                for (let coding of classifier.coding) {
                                                    if (coding.system === "http://hl7.org/fhir/artifact-url-classifier") {
                                                        coding.system = "http://terminology.hl7.org/CodeSystem/artifact-url-classifier"
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                return <Table.Row key={index}>
                                    {editMode ?
                                        <>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <TextField style={{ width: "100%" }} multiline
                                                    className="inputField" type='text' label={'URL'}
                                                    size="small" variant='outlined'
                                                    value={webLocation.url || ''}
                                                    onChange={(e) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newWebLocationArray = JSON.parse(JSON.stringify(prevState.webLocation));
                                                            newWebLocationArray[index]["url"] = e.target.value;
                                                            return { ...prevState, "webLocation": newWebLocationArray };
                                                        });
                                                    }} />
                                            </Table.Cell>
                                            <Table.Cell>
                                                <DropDownWithAdditions currentValue={webLocation.classifier || []}
                                                    labelText={'Pick URL Type'}
                                                    options={articleUrlTypeOptions.options}
                                                    optionsLookup={generateValueSetLookup("artifactUrlClassifierValueSet")}
                                                    setOptions={setArticleUrlTypeOptions}
                                                    setResourceElementStateForArray={setCitedArtifactState}
                                                    elementName={"webLocation"} subelementName={"classifier"} index={index}
                                                    multiple={true}
                                                />
                                                {(webLocation.classifier?.length === 1 &&
                                                    articleUrlTypeLookup[webLocation.classifier.coding?.[0]?.code]) &&
                                                    <p><b>Tool tip:</b> used for {articleUrlTypeLookup[webLocation.classifier.coding[0]?.code].definition} </p>
                                                }
                                                {webLocation.classifier?.length > 1 &&
                                                    <p><b>Tool tip:</b> you have two or more URL types, you can remove any URL type that you no longer wish to keep.</p>
                                                }
                                            </Table.Cell>
                                        </>
                                        :
                                        <>
                                            <Table.Cell>
                                                <a href={webLocation.url} target="_blank" rel="noopener noreferrer"
                                                    style={{ wordBreak: "break-all" }}>{webLocation.url}</a>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {webLocation.classifier &&
                                                    getCodeableConceptDisplayForObjectOrArray(webLocation.classifier, articleUrlTypeLookup)
                                                }
                                            </Table.Cell>
                                        </>
                                    }
                                </Table.Row>;
                            })
                            }
                        </Table.Body>
                    </Table>
                    :
                    <span style={{ color: "#EE1111" }}>No data.</span>
                }
                <span>
                    {editMode &&
                        <Button className="formButton" style={{ color: "#000000" }}
                            content={"+ Add URL"}
                            onClick={() => {
                                setCitedArtifactState(prevState => {
                                    let newArray = [{}];
                                    if (prevState["webLocation"]) {
                                        newArray = prevState["webLocation"].concat([{}]);
                                    }
                                    return { ...prevState, "webLocation": newArray };
                                });
                            }} />
                    }

                </span>
            </div>
            <h3 id="contributors">Contributors</h3>
            {editMode ?
                <CitedArtifactContributorshipEntry startingValue={citedArtifactContributorshipState}
                    containedResourceDictionary={containedResourceDictionary}
                    setCitedArtifactContributorshipState={setCitedArtifactContributorshipState} foi={foi} />
                :
                <CitedArtifactContributorshipDisplay contributorship={citationResourceState.citedArtifact?.contributorship}
                    containedResourceDictionary={containedResourceDictionary} />
            }
            <h3 id="classifiers">Classifiers</h3>
            <div className={`CitationClassifiersSubSection`} style={{ paddingLeft: "40px" }}>
                {editMode ?
                    <>
                        {citedArtifactClassificationsState?.simpleClassifications?.length >= 1 ?
                            <Table>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Type</Table.HeaderCell>
                                        <Table.HeaderCell>Classifiers</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {citedArtifactClassificationsState.simpleClassifications.map((classificationEntryX, index) => {
                                        let classificationEntry = classificationEntryX;
                                        if (editMode) {
                                            classificationEntry = JSON.parse(JSON.stringify(classificationEntryX));
                                            if (classificationEntry.type) {
                                                if (Array.isArray(classificationEntry.type.coding)) {
                                                    for (let coding of classificationEntry.type.coding) {
                                                        if (coding.system === "http://hl7.org/fhir/cited-artifact-classification-type") {
                                                            coding.system = "http://terminology.hl7.org/CodeSystem/cited-artifact-classification-type"
                                                        }
                                                    }
                                                }
                                            }
                                            if (Array.isArray(classificationEntry.classifier)) {
                                                for (let classifier of classificationEntry.classifier) {
                                                    if (Array.isArray(classifier.coding)) {
                                                        for (let coding of classifier.coding) {
                                                            if (coding.system === "http://hl7.org/fhir/citation-artifact-classifier" ||
                                                                coding.system === "http://build.fhir.org/valueset-citation-artifact-classifier.html"
                                                            ) {
                                                                coding.system = "http://terminology.hl7.org/CodeSystem/citation-artifact-classifier"
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        return <Table.Row key={index}>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <DropDownWithAdditions currentValue={classificationEntry.type || ""}
                                                    labelText={'Pick Classification Type'}
                                                    options={classificationTypeOptions.options}
                                                    optionsLookup={generateValueSetLookup("citedArtifactClassificationTypeValueSet")}
                                                    setOptions={setClassificationTypeOptions}
                                                    setResourceElementStateForArray={setCitedArtifactClassificationsState}
                                                    elementName={"simpleClassifications"} subelementName={"type"} index={index}
                                                    multiple={false}
                                                />
                                            </Table.Cell>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                <DropDownWithAdditions currentValue={classificationEntry.classifier || []}
                                                    labelText={'Add Classifiers'}
                                                    options={classifierOptions.options}
                                                    optionsLookup={generateValueSetLookup("citationArtifactClassifierValueSet")}
                                                    setOptions={setClassifierOptions}
                                                    setResourceElementStateForArray={setCitedArtifactClassificationsState}
                                                    elementName={"simpleClassifications"} subelementName={"classifier"} index={index}
                                                    multiple={true}
                                                />
                                            </Table.Cell>
                                        </Table.Row>

                                    })
                                    }
                                </Table.Body>

                            </Table>
                            :
                            <span style={{ color: "#EE1111" }}>No data.</span>
                        }
                        <span>
                            <Button className="formButton" style={{ color: "#000000" }}
                                content="+ Add Classifier"
                                onClick={() => {
                                    setCitedArtifactClassificationsState(prevState => {
                                        let newArray = [{}];
                                        if (prevState["simpleClassifications"]) {
                                            newArray = prevState["simpleClassifications"].concat([{}]);
                                        }
                                        return { ...prevState, "simpleClassifications": newArray };
                                    });
                                }} />
                        </span>
                    </>
                    :
                    <>{citedArtifactClassificationsState?.simpleClassifications?.length > 0 ?
                        <>
                            <Table>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Type</Table.HeaderCell>
                                        <Table.HeaderCell>Classifiers</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {citedArtifactClassificationsState.simpleClassifications.map((classificationEntry, index) => {
                                        return <Table.Row key={index}>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                {classificationEntry.type && simpleDisplayStringFromFHIRCodeableConcept(classificationEntry.type)}
                                            </Table.Cell>
                                            <Table.Cell style={{ verticalAlign: "top" }}>
                                                {classificationEntry.classifier && getCodeableConceptDisplayForObjectOrArray(classificationEntry.classifier)}
                                            </Table.Cell>
                                        </Table.Row>
                                    })
                                    }
                                </Table.Body>
                            </Table>
                        </>
                        :
                        <span style={{ color: "#EE1111" }}>No data.</span>
                    }</>
                }
            </div>
            <h3 id="mesh-headings">MeSH Headings (and other complex classifiers)</h3>
            <div className={`CitationMeshHeadingsClassifiersSubSection`} style={{ paddingLeft: "40px" }}>
                {citedArtifactClassificationsState.complexClassifications?.length > 0 ?
                    <>
                        {citedArtifactClassificationsState.complexClassifications.map((classification, classificationIndex) => {

                            let meshHeading = false;
                            let meshHeadingSet = [];
                            let extraClassifications = [];
                            let classificationType = "[Undeclared]";
                            if (classification["type"]) {
                                if (classification["type"].coding?.length > 0) {
                                    if (classification["type"].coding[0].code) {
                                        classificationType = classification["type"].coding[0].code;
                                    } else if (classification["type"].coding[0].display) {
                                        classificationType = classification["type"].coding[0].display;
                                    }
                                }
                                if (classificationType === "[Undeclared]" && classification["type"].text) {
                                    classificationType = classification["type"].text;
                                }
                                if (classificationType !== "[Undeclared]") {
                                    if (classificationType.toLowerCase().replace(" ", "-") === "mesh-heading") {
                                        meshHeading = true;
                                    }
                                }
                            }
                            let classifiersCoded = []; //classifiersWithCodingJsonArray, to be kept for when saving back to JSON since this isn't currently editable
                            let classifiersCodedDisplay = ""; //classifiersCodeableConceptsWithCodingDisplay, This will be the display in Edit Mode and doesn't include the text elements
                            let classifiersCodeableConceptsWithCodingDisplayArray = [];
                            let classifiersTexts = []; //classifiersTextsWithNoCodingArray, To populate the dropdown in edit mode with codeableConcepts with only a text element, doesn't include text elements that also have a coding
                            let classifiersAllDisplay = "";  //classifiersAllCodeableConceptsDisplay, This will be the display in Read Mode
                            let classifiersAllCodeableConceptsDisplayArray = [];
                            for (let classifierIndex in classification["classifier"]) {
                                let classifier = classification["classifier"][classifierIndex];
                                let singleClassifierDisplayArray = [];
                                let hasACoding = false;
                                for (let classifierCodingIndex in classifier.coding) {
                                    if (classifier.coding[classifierCodingIndex].display) {
                                        hasACoding = true;
                                        singleClassifierDisplayArray.push(classifier.coding[classifierCodingIndex].display); //
                                    } else if (classifier.coding[classifierCodingIndex].code) {
                                        hasACoding = true;
                                    }
                                }

                                if (hasACoding) { //If a code or display was found in the codeable concept
                                    classifiersCoded.push(classifier);
                                    if (classifier.text) {
                                        singleClassifierDisplayArray.push(classifier.text);
                                    }
                                    if (singleClassifierDisplayArray.length > 0) {
                                        classifiersCodeableConceptsWithCodingDisplayArray.push(singleClassifierDisplayArray.join(", "));
                                    }
                                } else if (classifier.text) { //If only a text element for the codeable concept
                                    classifiersTexts.push(classifier.text);
                                    singleClassifierDisplayArray.push(classifier.text);
                                }

                                if (singleClassifierDisplayArray.length > 0) {
                                    classifiersAllCodeableConceptsDisplayArray.push(singleClassifierDisplayArray.join(", "));
                                }
                            }
                            classifiersCodedDisplay = classifiersCodeableConceptsWithCodingDisplayArray.join("; ");
                            classifiersAllDisplay = classifiersAllCodeableConceptsDisplayArray.join("; ");
                            let metaTags = [];
                            for (let artifactAssessmentIndex in classification.artifactAssessment) {
                                let artifactAssessment = classification.artifactAssessment[artifactAssessmentIndex];
                                let metaTag = { "contents": [] };

                                metaTag["display"] = artifactAssessment.display || "";

                                if (artifactAssessment.reference) {
                                    if (artifactAssessment.reference.indexOf("#") === 0) {
                                        let containedClassification = containedResources[artifactAssessment.reference.replace("#", "")];
                                        if (containedClassification?.resourceType === "ArtifactAssessment") {
                                            [metaTag, meshHeading] = createMetaTagFromClassificationArtifactAssessmentJson(metaTag, containedClassification, meshHeading);
                                        }
                                    }
                                }
                                if (metaTag.contents.length === 0 && metaTag.display === "") {
                                    metaTag = "EMPTY";
                                }
                                if (metaTag !== "EMPTY") {
                                    metaTags.push(metaTag);
                                }
                            }
                            if (meshHeading) {
                                //mesh heading classifiers
                                //"classifiersCodeableConceptsWithCodingDisplayArray": classifiersCodeableConceptsWithCodingDisplayArray,
                                //"classifiersAllCodeableConceptsDisplayArray": classifiersAllCodeableConceptsDisplayArray,
                                meshHeadingSet.push({ "originalJson": classification, "classificationType": classificationType, "classifiersCoded": classifiersCoded, "classifiersCodedDisplay": classifiersCodedDisplay, "classifiersTexts": classifiersTexts, "classifiersAllDisplay": classifiersAllDisplay, "metaTags": metaTags });
                            } else if (metaTags.length > 0) {   //classification.artifactAssessment?.length > 0
                                //compound classifiers
                                //"classifiersCodeableConceptsWithCodingDisplayArray": classifiersCodeableConceptsWithCodingDisplayArray,
                                //"classifiersAllCodeableConceptsDisplayArray": classifiersAllCodeableConceptsDisplayArray,
                                let extraClassificationEntry = { "originalJson": classification, "classificationType": classificationType, "classifiersCoded": classifiersCoded, "classifiersCodedDisplay": classifiersCodedDisplay, "classifiersTexts": classifiersTexts, "classifiersAllDisplay": classifiersAllDisplay, "metaTags": metaTags };
                                extraClassifications.push(extraClassificationEntry);
                            }

                            let classificationTypeDisplay = simpleDisplayStringFromFHIRCodeableConcept(classification.type);
                            return <div key={classificationIndex}>
                                {meshHeadingSet.length > 0 &&
                                    (meshHeadingSet[0].metaTags.length > 0 || meshHeadingSet[0].classifiersAllDisplay) &&
                                    <Table>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.HeaderCell>Classifiers</Table.HeaderCell>
                                                <Table.HeaderCell>Qualifiers</Table.HeaderCell>
                                                <Table.HeaderCell>Attribution</Table.HeaderCell>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {meshHeadingSet[0].classifiersAllDisplay &&
                                                <Table.Row>
                                                    <Table.Cell style={{ verticalAlign: "top" }}>{meshHeadingSet[0].classifiersAllDisplay}</Table.Cell>
                                                    <Table.Cell style={{ verticalAlign: "top" }}></Table.Cell>
                                                    <Table.Cell style={{ verticalAlign: "top" }}></Table.Cell>
                                                </Table.Row>
                                            }
                                            {meshHeadingSet[0].metaTags.length > 0 && meshHeadingSet[0].metaTags[0].contents.map((content, contentIndex) => {
                                                return <Table.Row key={contentIndex}>
                                                    <Table.Cell style={{ verticalAlign: "top" }}>{content.classifiersDisplayString}</Table.Cell>
                                                    <Table.Cell style={{ verticalAlign: "top" }}>{content.qualifiersDisplay}</Table.Cell>
                                                    <Table.Cell style={{ verticalAlign: "top" }}>
                                                        {content.authorDisplay && <><>Created by: </>{content.authorDisplay}<br /></>}
                                                        {content.freeToShare ?
                                                            <>Free to share</>
                                                            :
                                                            <>{content.freeToShare === false && <>Not free to share</>}</>
                                                        }
                                                    </Table.Cell>
                                                </Table.Row>
                                            })
                                            }
                                        </Table.Body>
                                    </Table>
                                }
                                {extraClassifications.length > 0 &&
                                    extraClassifications.map((extraClassification, extraClassificationIndex) => {
                                        return <div key={extraClassificationIndex}>
                                            {extraClassification.metaTags.map((metaTag, metaTagIndex) => {
                                                return <div key={metaTagIndex}>
                                                    <br />
                                                    <h4>{metaTag.display ? metaTag.display : <>[Undeclared Title]</>}</h4>
                                                    {metaTag.copyright && <>Copyright: {metaTag.copyright}</>}
                                                    <Table>
                                                        <Table.Header>
                                                            <Table.Row>
                                                                <Table.HeaderCell>Type</Table.HeaderCell>
                                                                <Table.HeaderCell>Classifiers</Table.HeaderCell>
                                                                <Table.HeaderCell>Qualifiers</Table.HeaderCell>
                                                                <Table.HeaderCell>Attribution</Table.HeaderCell>
                                                            </Table.Row>
                                                        </Table.Header>
                                                        <Table.Body>
                                                            {extraClassification.classifiersAllDisplay &&
                                                                <Table.Row>
                                                                    <Table.Cell style={{ verticalAlign: "top" }}>{classificationTypeDisplay}</Table.Cell>
                                                                    <Table.Cell style={{ verticalAlign: "top" }}>{extraClassification.classifiersAllDisplay}</Table.Cell>
                                                                    <Table.Cell style={{ verticalAlign: "top" }}></Table.Cell>
                                                                    <Table.Cell style={{ verticalAlign: "top" }}></Table.Cell>
                                                                </Table.Row>
                                                            }
                                                            {metaTag.contents.map((content, contentIndex) => {
                                                                return <Table.Row key={contentIndex}>
                                                                    <Table.Cell style={{ verticalAlign: "top" }}>{content.typeDisplay ? content.typeDisplay : extraClassification.classificationType}</Table.Cell>
                                                                    <Table.Cell style={{ verticalAlign: "top" }}>{content.classifiersDisplayString}</Table.Cell>
                                                                    <Table.Cell style={{ verticalAlign: "top" }}>{content.qualifiersDisplay}</Table.Cell>
                                                                    <Table.Cell style={{ verticalAlign: "top" }}>
                                                                        {content.authorDisplay && <><>Created by: </>{content.authorDisplay}<br /></>}
                                                                        {content.freeToShare ?
                                                                            <>Free to share</>
                                                                            :
                                                                            <>{content.freeToShare === false && <>Not free to share</>}</>
                                                                        }
                                                                    </Table.Cell>
                                                                </Table.Row>
                                                            })
                                                            }
                                                        </Table.Body>
                                                    </Table>
                                                    <br />
                                                </div>
                                            })}
                                        </div>
                                    })}

                            </div>
                        })}
                    </>
                    :
                    <span style={{ color: "#EE1111" }}>No data.</span>
                }
            </div>
            <h3 id="status">Status</h3>
            <div className={`CitationStatusSubSection`} style={{ paddingLeft: "40px" }}>
                <span>
                    <b style={{ fontSize: "1.1em" }}>Current state: </b>
                    {editMode ?
                        <ArrayEntry datatype='CodeableConcept' elementName='currentState'
                            fieldLabel='Current State'
                            startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                            codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet("citedArtifactStatusTypeValueSet")}
                            startingValue={citedArtifactState?.currentState} setResourceState={setCitedArtifactState} />
                        :
                        <>{citedArtifactState?.currentState && getCodeableConceptDisplayForObjectOrArray(citedArtifactState.currentState)}</>
                    }
                </span>
                <br />
                <span><b style={{ fontSize: "1.1em" }}>Status dates: </b>
                </span>
                {citedArtifactState?.statusDate?.length >= 1 ?
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>State</Table.HeaderCell>
                                <Table.HeaderCell>Actual/Expected</Table.HeaderCell>
                                <Table.HeaderCell>Start</Table.HeaderCell>
                                <Table.HeaderCell>End</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {citedArtifactState.statusDate.map((statusDateEntryX, index) => {
                                let statusDateEntry = statusDateEntryX;
                                if (editMode) {
                                    statusDateEntry = JSON.parse(JSON.stringify(statusDateEntryX));
                                    if (statusDateEntry.activity) {
                                        if (Array.isArray(statusDateEntry.activity.coding)) {
                                            for (let coding of statusDateEntry.activity.coding) {
                                                if (coding.system === "http://hl7.org/fhir/cited-artifact-status-type") {
                                                    coding.system = "http://terminology.hl7.org/CodeSystem/cited-artifact-status-type"
                                                }
                                            }
                                        }
                                    }
                                }
                                return <Table.Row key={index}>
                                    <Table.Cell>{editMode ?
                                        <DropDownWithAdditions currentValue={statusDateEntry.activity || ""}
                                            labelText={'Pick Activity'}
                                            options={citedArtifactStatusTypeOptions.options}
                                            optionsLookup={generateValueSetLookup("citedArtifactStatusTypeValueSet")}
                                            setOptions={setCitedArtifactStatusTypeOptions}
                                            setResourceElementStateForArray={setCitedArtifactState}
                                            elementName={"statusDate"} subelementName={"activity"} index={index}
                                            multiple={false}
                                        />
                                        :
                                        <>{simpleDisplayStringFromFHIRCodeableConcept(statusDateEntry.activity)}</>
                                    }</Table.Cell>
                                    <Table.Cell>
                                        {editMode ?
                                            <fieldset style={{
                                                width: "300px", display: "inline", border: "0px",
                                                verticalAlign: "bottom", padding: "0px"
                                            }}>
                                                &nbsp;&nbsp;&nbsp;
                                                <input type="radio" name={"statusDataActualExpected" + index.toString()}
                                                    value="true" checked={statusDateEntry.actual}
                                                    onChange={(e) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newStatusDateArray = JSON.parse(JSON.stringify(prevState.statusDate));
                                                            newStatusDateArray[index]["actual"] = e.target.checked;
                                                            return { ...prevState, "statusDate": newStatusDateArray };
                                                        });
                                                    }} />
                                                <label> Actual</label>
                                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                                <input type="radio" name={"statusDataActualExpected" + index.toString()}
                                                    value="false" checked={statusDateEntry.actual === false}
                                                    onChange={(e) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newStatusDateArray = JSON.parse(JSON.stringify(prevState.statusDate));
                                                            newStatusDateArray[index]["actual"] = !e.target.checked;
                                                            return { ...prevState, "statusDate": newStatusDateArray };
                                                        });
                                                    }} />
                                                <label> Expected</label>
                                            </fieldset>
                                            :
                                            <>{
                                                (statusDateEntry.actual !== undefined && statusDateEntry.actual !== null) &&
                                                <>{statusDateEntry.actual ? <>Actual</> : <>Expected</>}</>
                                            }
                                            </>
                                        }
                                    </Table.Cell>
                                    <Table.Cell>
                                        {editMode ?
                                            <TextField style={{ width: "200px" }} multiline className="inputField" type='text'
                                                label={'Start Date (YYYY-MM-DD)'} size="small" variant='outlined'
                                                value={statusDateEntry.period?.start || ""}
                                                onChange={(e) => {
                                                    setCitedArtifactState(prevState => {
                                                        let newStatusDateArray = JSON.parse(JSON.stringify(prevState.statusDate));
                                                        if (!newStatusDateArray[index]["period"]) {
                                                            newStatusDateArray[index]["period"] = { "start": e.target.value };
                                                        } else {
                                                            newStatusDateArray[index]["period"]["start"] = e.target.value;
                                                        }
                                                        return { ...prevState, "statusDate": newStatusDateArray };
                                                    });
                                                }} />
                                            :
                                            <>{statusDateEntry.period?.start && getDateTimeDisplay(statusDateEntry.period.start)}</>

                                        }
                                    </Table.Cell>
                                    <Table.Cell>
                                        {editMode ?
                                            <TextField style={{ width: "200px" }} multiline className="inputField" type='text'
                                                label={'End Date (YYYY-MM-DD)'} size="small" variant='outlined'
                                                value={statusDateEntry.period?.end || ""}
                                                onChange={(e) => {
                                                    setCitedArtifactState(prevState => {
                                                        let newStatusDateArray = JSON.parse(JSON.stringify(prevState.statusDate));
                                                        if (!newStatusDateArray[index]["period"]) {
                                                            newStatusDateArray[index]["period"] = { "end": e.target.value };
                                                        } else {
                                                            newStatusDateArray[index]["period"]["end"] = e.target.value;
                                                        }
                                                        return { ...prevState, "statusDate": newStatusDateArray };
                                                    });
                                                }} />
                                            :
                                            <>{statusDateEntry.period?.end && getDateTimeDisplay(statusDateEntry.period.end)}</>
                                        }
                                    </Table.Cell>
                                </Table.Row>;
                            })
                            }
                        </Table.Body>
                    </Table>
                    :
                    <span style={{ color: "#EE1111" }}>No data.</span>
                }
                <span>
                    {editMode &&
                        <Button className="formButton" style={{ color: "#000000" }}
                            content="+ Add Status Date"
                            onClick={() => {
                                setCitedArtifactState(prevState => {
                                    let newArray = [{}];
                                    if (prevState["statusDate"]) {
                                        newArray = prevState["statusDate"].concat([{}]);
                                    }
                                    return { ...prevState, "statusDate": newArray };
                                });
                            }} />
                    }

                </span>
            </div>
            <h3 id="modifiers">Modifiers</h3>
            <div className={`CitationModifiersSubSection`} style={{ paddingLeft: "40px" }}>
                {(editMode || citedArtifactState?.dateAccessed) && <>
                    <span>
                        <b style={{ fontSize: "1.1em" }}>Date accessed: </b>
                        {editMode ?
                            <TextField style={{ width: "280px" }} multiline className="inputField" type='text'
                                label={'Date accessed (YYYY-MM-DD)'} size="small" variant='outlined'
                                value={citedArtifactState.dateAccessed}
                                onChange={(e) => {
                                    setCitedArtifactState(prevState => {
                                        return { ...prevState, "dateAccessed": e.target.value };
                                    });
                                }} />
                            :
                            <>{citedArtifactState.dateAccessed && getDateTimeDisplay(citedArtifactState.dateAccessed)}</>
                        }
                    </span>
                    <br />
                </>}
                {(editMode || citedArtifactState?.version) && <>
                    <span><b style={{ fontSize: "1.1em" }}>Version: </b>
                        {editMode ?
                            <TextField style={{ width: "280px" }} multiline className="inputField"
                                type='text' label={'Version'} size="small"
                                variant='outlined'
                                value={typeof citedArtifactState?.version === "string" ?
                                    citedArtifactState.version
                                    :
                                    JSON.stringify(citedArtifactState?.version)
                                }
                                onChange={(e) => {
                                    setCitedArtifactState(prevState => {
                                        return { ...prevState, "version": e.target.value };
                                    });
                                }}
                            />
                            :
                            <>{typeof citedArtifactState?.version === "string" ?
                                <>{citedArtifactState.version}</>
                                :
                                <>{JSON.stringify(citedArtifactState?.version)}</>
                            }</>
                        }
                    </span>
                    <br />
                </>}
                {(editMode || citedArtifactState?.part) &&
                    <>
                        <span><b style={{ fontSize: "1.1em" }}>Part: </b></span>
                        {editMode ?
                            <CitedArtifactPartEntry startingValue={citedArtifactState?.part}
                                setCitedArtifactState={setCitedArtifactState} />
                            :
                            <>
                                {citedArtifactState?.part?.type &&
                                    <>
                                        <br />
                                        <span style={{ paddingLeft: "40px" }}>
                                            <b style={{ fontSize: "1.1em" }}>Type: </b>
                                            <>{simpleDisplayStringFromFHIRCodeableConcept(citedArtifactState.part.type)}</>
                                        </span>
                                    </>}
                                <br />
                                <span style={{ paddingLeft: "40px" }}>
                                    <b style={{ fontSize: "1.1em" }}>Value: </b>
                                    <>{citedArtifactState?.part?.value}</>
                                </span>
                            </>
                        }
                    </>
                }
                {(editMode || citedArtifactState?.baseCitation) && <>
                    <br />
                    <span><b style={{ fontSize: "1.1em" }}>Base citation: </b>
                        {editMode ?
                            <>
                                <br />
                                <span style={{ paddingLeft: "40px" }}>
                                    <b style={{ fontSize: "1.1em" }}>Reference URL: </b>
                                    <TextField style={{ width: "280px" }} multiline
                                        className="inputField" type='text'
                                        label={'Base citation reference URL'}
                                        size="small" variant='outlined'
                                        value={citedArtifactState?.baseCitation?.reference}
                                        onChange={(e) => {
                                            setCitedArtifactState(prevState => {
                                                return {
                                                    ...prevState, "baseCitation": { ...prevState, "reference": e.target.value }
                                                };
                                            });
                                        }} />
                                </span>
                                {citedArtifactState?.baseCitation?.identifier &&
                                    <p>The baseCitation has an identifier (editable in the JSON) with a value of: {displayStringFromFHIRIdentifier(citedArtifactState.baseCitation.identifier)}</p>
                                }
                                <br />
                                <span style={{ paddingLeft: "40px" }}>
                                    <b style={{ fontSize: "1.1em" }}>Display: </b>
                                    <TextField style={{ width: "280px" }} multiline
                                        className="inputField" type='text'
                                        label={'Base citation display'} size="small" variant='outlined'
                                        value={citedArtifactState?.baseCitation?.display}
                                        onChange={(e) => {
                                            setCitedArtifactState(prevState => {
                                                return {
                                                    ...prevState, "baseCitation": { ...prevState, "display": e.target.value }
                                                };
                                            });
                                        }} />
                                </span>
                            </>
                            :
                            <><DisplayFromFHIRReference reference={citedArtifactState?.baseCitation}
                                containedResourceJson={citedArtifactState?.baseCitation?.reference && containedResourceDictionary[citedArtifactState.baseCitation.reference]} /></>
                        }
                    </span>
                    <br />
                </>}
                <br />
                {(editMode || citedArtifactState?.note?.length > 0) &&
                    <div>
                        <p><b style={{ fontSize: "1.1em" }}>Notes: </b></p>
                        {editMode ?
                            <>
                                <ArrayEntry datatype='Annotation' elementName='note' fieldLabel='Note'
                                    startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed
                                    startingValue={citedArtifactState?.note} setResourceState={setCitedArtifactState} />
                            </>
                            :
                            <>
                                <DisplayElementNotes element={citedArtifactState?.note} />
                            </>
                        }
                    </div>}
            </div>
            <h3 id="related-articles">Related Articles</h3>
            <div className={`CitationRelatedArticlesSubSection`} style={{ paddingLeft: "40px" }}>
                {citedArtifactState?.relatesTo?.length >= 1 ?
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Relation Type</Table.HeaderCell>
                                <Table.HeaderCell>Target Classifier</Table.HeaderCell>
                                <Table.HeaderCell>Target</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {citedArtifactState.relatesTo.map((relatesToEntryX, index) => {
                                let relatesToEntry = relatesToEntryX;
                                if (editMode) {
                                    relatesToEntry = JSON.parse(JSON.stringify(relatesToEntryX));
                                    if (Array.isArray(relatesToEntry.classifier)) {
                                        for (let classifier of relatesToEntry.classifier) {
                                            if (Array.isArray(classifier.coding)) {
                                                for (let coding of classifier.coding) {
                                                    if (coding.system === "http://hl7.org/fhir/citation-artifact-classifier" ||
                                                        coding.system === "http://build.fhir.org/valueset-citation-artifact-classifier.html"
                                                    ) {
                                                        coding.system = "http://terminology.hl7.org/CodeSystem/citation-artifact-classifier"
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                return <Table.Row key={index}>
                                    <Table.Cell style={{ verticalAlign: "top" }}>
                                        <>
                                            {editMode ?
                                                <DropDownWithAdditions currentValue={relatesToEntry.type || ""}
                                                    labelText={'Relation Type'}
                                                    options={artifactRelationshipTypeOptions.options}
                                                    optionsLookup={generateValueSetLookup("artifactRelationshipTypeValueSet")}
                                                    setOptions={setArtifactRelationshipTypeOptions}
                                                    setResourceElementStateForArray={setCitedArtifactState}
                                                    elementName={"relatesTo"} subelementName={"type"} index={index}
                                                    multiple={false}
                                                />
                                                :
                                                <>
                                                    {(relatesToEntry.type && typeof relatesToEntry.type === "object") ?
                                                        simpleDisplayStringFromFHIRCodeableConcept(relatesToEntry.type)
                                                        :
                                                        relatesToEntry.type}
                                                </>
                                            }
                                        </>
                                    </Table.Cell>
                                    <Table.Cell style={{ verticalAlign: "top" }}>
                                        {editMode ?
                                            <>
                                                <DropDownWithAdditions currentValue={relatesToEntry.classifier || []}
                                                    labelText={'Add Classifiers'}
                                                    options={artifactClassifierTypeOptions.options}
                                                    optionsLookup={generateValueSetLookup("citationArtifactClassifierValueSet")}
                                                    setOptions={setArtifactClassifierTypeOptions}
                                                    setResourceElementStateForArray={setCitedArtifactState}
                                                    elementName={"relatesTo"} subelementName={"classifier"} index={index}
                                                    multiple={true}
                                                />
                                            </>
                                            :
                                            <>{getCodeableConceptDisplayForObjectOrArray(relatesToEntry.classifier)}</>
                                        }
                                    </Table.Cell>
                                    <Table.Cell style={{ verticalAlign: "top" }}>
                                        {editMode ?
                                            <>
                                                <TextField style={{ width: "100%" }} multiline
                                                    className="inputField" type='text'
                                                    label={"Target Description"} size="small" variant='outlined'
                                                    value={relatesToEntry.display}
                                                    onChange={(e) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newRelatesToArray = JSON.parse(JSON.stringify(prevState.relatesTo));
                                                            newRelatesToArray[index]["display"] = e.target.value;
                                                            return { ...prevState, "relatesTo": newRelatesToArray };
                                                        });
                                                    }} />
                                                <br /><br />
                                                <TextField style={{ width: "100%" }} multiline
                                                    className="inputField" type='text'
                                                    label={"Target Citation"} size="small" variant='outlined'
                                                    value={relatesToEntry.citation}
                                                    onChange={(e) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newRelatesToArray = JSON.parse(JSON.stringify(prevState.relatesTo));
                                                            newRelatesToArray[index]["citation"] = e.target.value;
                                                            return { ...prevState, "relatesTo": newRelatesToArray };
                                                        });
                                                    }} />
                                                <br /><br />
                                                <TextField style={{ minWidth: "100%" }} multiline
                                                    className="inputField" type='text'
                                                    label={"Target URL"} size="small" variant='outlined'
                                                    value={relatesToEntry.targetUri || relatesToEntry.targetCanonical}
                                                    onChange={(e) => {
                                                        setCitedArtifactState(prevState => {
                                                            let newRelatesToArray = JSON.parse(JSON.stringify(prevState.relatesTo));
                                                            newRelatesToArray[index]["targetUri"] = e.target.value;
                                                            return { ...prevState, "relatesTo": newRelatesToArray };
                                                        });
                                                    }} />
                                            </>
                                            :
                                            <>
                                                {relatesToEntry.display}
                                                {(relatesToEntry.display && relatesToEntry.citation) && <br />}
                                                <DisplayFromFHIRMarkdown markdown={relatesToEntry.citation} />
                                                <>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                                    <b>
                                                        {relatesToEntry.targetReference &&
                                                            <DisplayFromFHIRReference reference={relatesToEntry.targetReference}
                                                                containedResourceJson={relatesToEntry.targetReference.reference && containedResourceDictionary[relatesToEntry.targetReference.reference]} />}
                                                    </b>
                                                    {relatesToEntry.targetAttachment &&
                                                        <DisplayFromFHIRAttachment attachment={relatesToEntry.targetAttachment} />
                                                    }
                                                    {relatesToEntry.targetUri &&
                                                        <DisplayFromFHIRUri uri={relatesToEntry.targetUri} />
                                                    }
                                                    {relatesToEntry.targetCanonical &&
                                                        <DisplayFromFHIRUri uri={relatesToEntry.targetCanonical} />}
                                                </>
                                            </>
                                        }
                                    </Table.Cell>
                                </Table.Row>;
                            })
                            }
                        </Table.Body>
                    </Table>
                    :
                    <span style={{ color: "#EE1111" }}>No data.</span>
                }
                <span>
                    {editMode &&
                        <Button className="formButton" style={{ color: "#000000" }}
                            content="+ Add Related Article"
                            onClick={() => {
                                setCitedArtifactState(prevState => {
                                    let newArray = [{}];
                                    if (prevState["relatesTo"]) {
                                        newArray = prevState["relatesTo"].concat([{}]);
                                    }
                                    return { ...prevState, "relatesTo": newArray };
                                });
                            }} />
                    }
                </span>
            </div>
        </>}
    </div>
}

export default CitationDisplay;
export {
    CitationClassificationEntry, CitationStatusDateEntry, CitationSummaryEntry,
    CitationCitedArtifactContributorshipSummaryEntry,
    CitationCitedArtifactContributorshipEntryEntry, ContributionInstanceEntry,
    CitedArtifactPublicationFormDisplay, CitedArtifactContributorshipDisplay
};